import { useEffect, useMemo, useRef, useState } from "react";
import {
  CERT_STATUS_LABEL,
  CONTRACT_KIND_LABEL,
  bySupplier,
  contractPosition,
  contractTotal,
  contractTotalIn,
  projectCertifiedIn,
  projectPaidIn,
  worksCertified,
  advancePaid,
  projectCertCount,
  projectTotal,
  certPaid,
  certOutstanding,
  certPayState,
  PAY_STATE_LABEL,
  CERT_SERIES_LABEL,
  CERT_SERIES_HINT,
  certSeries,
  seriesForContract,
  nextCertNumber,
  applySeries,
  isAdvanceCert,
  hasValuation,
  computeBuildup,
  previouslyCertifiedAuto,
  type CertBuildup,
  type CertSeries,
  type CertKind,
  type CertPayment,
  type CertStatus,
  type Contract,
  type ContractKind,
  type PaymentCert,
  type PaymentProject,
  type PaymentsData,
} from "../../payments/types";
import type { ContractSeed } from "../../payments/types";
import { exampleProject, exportPayments, importPayments, loadPayments, newCert, newContract, newProject, nowDate, projectFromContract, savePayments, uid } from "../../payments/store";
import { extractManyPdfs } from "../pdf/extractText";
import { assembleCertificate } from "../../payments/assembleCertificate";
import { assembleSupplierStatement } from "../../payments/assembleStatement";
import { ensureWorkspace, getPass, getRemote, getWorkspace, putRemote, setPass, setWorkspace, workspaceLink } from "../ledgerSync";
import { ShareButtons } from "./ShareButtons";
import { createReview, pushSnapshot, getReview, setRequestReceipt, attachToReview } from "../reviewApi";
import FileAttach, { useDiscardedFiles } from "./FileAttach";
import { fileUrl, uploadFile, deleteFile, fileSize } from "../filesApi";
import { sendEmail, blobToBase64, parseEmails, type SentEmail } from "../emailApi";
import { certRecordFor, listAtelierProjects, pushCertificate, type AtelierProjectRef } from "../atelierSync";
import { renderPdfBlob } from "../print/printContract";
import { AuthPanel } from "./AuthPanel";
import { getMe, getMembers, getMyLedgers, logout, setMember, type Member } from "../authApi";
import { generateDocx } from "../docx/generateDocx";
import { exportPdf } from "../print/printContract";
import { getRates, convert, type FxRates } from "../fx";

const numStr = (n?: number) => (n == null || Number.isNaN(n) ? "" : String(n));
const num = (v: string) => (v.trim() === "" ? NaN : Number(v));
const fmt = (n: number, c: string) => (isFinite(n) ? `${c} ${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : `${c} —`);

/** Trim the surrounding whitespace/transparency around a signature so the strokes
 * fill the box (an image with a big empty margin otherwise renders tiny). Darkens
 * faint ink slightly so it reads crisply. Returns a tight-cropped PNG data URI. */
function cropSignature(img: HTMLImageElement): string | null {
  try {
    const w = img.naturalWidth, h = img.naturalHeight;
    const cv = document.createElement("canvas"); cv.width = w; cv.height = h;
    const g = cv.getContext("2d"); if (!g) return null;
    g.drawImage(img, 0, 0);
    const px = g.getImageData(0, 0, w, h).data;
    let minX = w, minY = h, maxX = -1, maxY = -1;
    const isInk = (i: number) => px[i + 3] > 40 && (px[i] + px[i + 1] + px[i + 2]) / 3 < 225;
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      if (isInk((y * w + x) * 4)) { if (x < minX) minX = x; if (x > maxX) maxX = x; if (y < minY) minY = y; if (y > maxY) maxY = y; }
    }
    if (maxX < 0) return null; // no ink found
    const pad = Math.max(2, Math.round(Math.min(w, h) * 0.05));
    minX = Math.max(0, minX - pad); minY = Math.max(0, minY - pad);
    maxX = Math.min(w - 1, maxX + pad); maxY = Math.min(h - 1, maxY + pad);
    const cw = maxX - minX + 1, ch = maxY - minY + 1;
    if (cw < 8 || ch < 4) return null;
    const out = document.createElement("canvas"); out.width = cw; out.height = ch;
    const og = out.getContext("2d"); if (!og) return null;
    og.drawImage(cv, minX, minY, cw, ch, 0, 0, cw, ch);
    return out.toDataURL("image/png");
  } catch {
    return null;
  }
}

/** Default-select the most useful project — the one with the most certificates
 * (so an empty "New Project" never hides a real one), falling back to the first. */
function bestProjectId(projects: PaymentsData["projects"]): string | null {
  if (!projects.length) return null;
  const ranked = [...projects].sort((a, b) => projectCertCount(b) - projectCertCount(a));
  return ranked[0].id;
}

const SEL_KEY = "cg:payments-sel";
/** Hand-off from another screen: open on this project, and scroll to this contract. */
export interface PaymentsFocus { projectId: string; contractId?: string; certId?: string }

export function PaymentsApp({ contractSeed, autoLink, focus }: { contractSeed?: ContractSeed; autoLink?: boolean; focus?: PaymentsFocus | null }) {
  const [data, setData] = useState<PaymentsData>(loadPayments);
  // Open on the project we were sent to, else the one last viewed, else the fullest.
  const [selId, setSelId] = useState<string | null>(() => {
    const projects = loadPayments().projects;
    let stored: string | null = null;
    try { stored = localStorage.getItem(SEL_KEY); } catch { /* ignore */ }
    const want = focus?.projectId || stored;
    return want && projects.some((p) => p.id === want) ? want : bestProjectId(projects);
  });
  useEffect(() => { try { if (selId) localStorage.setItem(SEL_KEY, selId); } catch { /* ignore */ } }, [selId]);
  // Scroll the hand-off contract into view once the project renders.
  useEffect(() => {
    if (!focus?.contractId) return;
    const t = setTimeout(() => document.getElementById(`contract-${focus.contractId}`)?.scrollIntoView({ block: "center", behavior: "smooth" }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus?.contractId, selId]);
  const [editing, setEditing] = useState<{ contractId: string; cert: PaymentCert } | null>(null);
  // Certificate being shared (its live link modal). `busy` while the link is minted.
  // `forReceipt` = sent to the payee to confirm receipt of payment (sign + photo).
  const [sharing, setSharing] = useState<{ contractId: string; cert: PaymentCert; id: string; busy: boolean; forReceipt?: boolean } | null>(null);
  const [shareErr, setShareErr] = useState<string | null>(null);
  const [shareCopied, setShareCopied] = useState(false);
  const [addingContract, setAddingContract] = useState<Contract | null>(null);
  // When set, the contract form edits this contract instead of adding one.
  const [editingContractId, setEditingContractId] = useState<string | null>(null);
  // Atelier (studio cockpit): the projects it offers to link to, and the last sync outcome.
  const [atelierProjects, setAtelierProjects] = useState<AtelierProjectRef[] | null>(null);
  const [atelierMsg, setAtelierMsg] = useState<string | null>(null);
  const atelierTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const [supplierFilter, setSupplierFilter] = useState<string | null>(null);
  const importRef = useRef<HTMLInputElement>(null);
  const quoteRef = useRef<HTMLInputElement>(null);
  const sigRef = useRef<HTMLInputElement>(null);
  const contractScanRef = useRef<HTMLInputElement>(null);
  const [scanContractId, setScanContractId] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [fx, setFx] = useState<FxRates | null>(null);
  const [scanMsg, setScanMsg] = useState<string | null>(null);
  const [ws, setWs] = useState<string | null>(getWorkspace);
  const [sync, setSync] = useState<"local" | "syncing" | "synced" | "offline">(getWorkspace() ? "syncing" : "local");
  const [copied, setCopied] = useState(false);
  const versionRef = useRef(0);
  const lastSynced = useRef<string>("");
  const [protectedLedger, setProtectedLedger] = useState(false);
  const [user, setUser] = useState<string | null>(null);
  const [authPrompt, setAuthPrompt] = useState<string | null>(null);
  const [syncNonce, setSyncNonce] = useState(0);
  const [accessMsg, setAccessMsg] = useState<string | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [isOwner, setIsOwner] = useState(false);
  const [readOnly, setReadOnly] = useState(false);

  useEffect(() => { getMe().then((m) => setUser(m.email)); }, []);
  // On login, if no workspace is loaded yet, pull the account's own ledger so it
  // appears automatically (no link/passcode needed) on any device.
  useEffect(() => {
    if (!user || ws) return;
    getMyLedgers().then((r) => { const first = r.workspaces?.[0]; if (first) { setWorkspace(first); setWs(first); } }).catch(() => {});
  }, [user, ws]);
  useEffect(() => { getRates().then(setFx); }, []);

  // One-time migration: move a signature from the old localStorage key into the
  // ledger data so it persists, exports, and syncs like the rest of the ledger.
  useEffect(() => {
    try {
      const old = localStorage.getItem("cg:signature");
      if (old) { setData((d) => (d.signature ? d : { ...d, signature: old })); localStorage.removeItem("cg:signature"); }
    } catch {}
  }, []);

  // One-time tight-crop of an already-stored signature (uploaded before cropping
  // existed) so it fills the signature line instead of floating tiny in whitespace.
  const recropped = useRef(false);
  useEffect(() => {
    if (recropped.current || !data.signature) return;
    recropped.current = true;
    const sig = data.signature;
    const img = new Image();
    img.onload = () => {
      const c = cropSignature(img);
      if (!c || c === sig) return;
      // Update the stored signature AND any certificate already stamped with the
      // old (whitespace-padded) version, so already-signed certs get the crisp one.
      setData((d) => {
        if (d.signature !== sig) return d;
        return {
          ...d,
          signature: c,
          projects: d.projects.map((p) => ({ ...p, contracts: p.contracts.map((ct) => ({ ...ct, certificates: ct.certificates.map((x) => (x.authorisedSignature === sig ? { ...x, authorisedSignature: c } : x)) })) })),
        };
      });
    };
    img.src = sig;
  }, [data.signature]);

  // Local cache autosave (offline fallback / no-sync mode).
  useEffect(() => {
    const t = setTimeout(() => savePayments(data), 300);
    return () => clearTimeout(t);
  }, [data]);

  // On (re)joining a workspace, pull the cloud ledger; if none, push local.
  useEffect(() => {
    if (!ws) return;
    let cancelled = false;
    setAccessMsg(null);
    (async () => {
      try {
        let r;
        try {
          r = await getRemote(ws);
        } catch (e: any) {
          if (e?.protected) {
            const p = window.prompt("This ledger is passcode-protected. Enter the passcode:");
            if (!p) { if (!cancelled) setSync("offline"); return; }
            setPass(p);
            r = await getRemote(ws); // retry with the passcode
          } else if (e?.needLogin) {
            if (!cancelled) { setAuthPrompt("Sign in to open this shared ledger."); setSync("offline"); }
            return;
          } else if (e?.forbidden) {
            if (!cancelled) { setAccessMsg("You don't have access to this ledger — ask the owner to invite your email."); setSync("offline"); }
            return;
          } else throw e;
        }
        if (cancelled) return;
        if (r.exists && r.data) {
          versionRef.current = r.version || 0;
          lastSynced.current = JSON.stringify(r.data);
          setProtectedLedger(!!r.protected);
          setIsOwner(!!r.isOwner);
          setReadOnly(r.role === "viewer");
          const pulled = r.data;
          setData(pulled);
          // Keep the project we're on (or were sent to) if it still exists.
          setSelId((cur) => { const want = focus?.projectId || cur; return want && pulled.projects.some((p) => p.id === want) ? want : bestProjectId(pulled.projects); });
          if (r.owned) getMembers(ws).then((m) => { if (m.members) setMembers(m.members); if (m.isOwner != null) setIsOwner(!!m.isOwner); });
        } else {
          const res = await putRemote(ws, data, undefined, user ? undefined : { setPasscode: getPass() });
          if ("version" in res) versionRef.current = res.version;
          lastSynced.current = JSON.stringify(data);
          setProtectedLedger(!user && !!getPass());
          setIsOwner(!!user);
          if (user) getMembers(ws).then((m) => { if (m.members) setMembers(m.members); });
        }
        if (!cancelled) setSync("synced");
      } catch {
        if (!cancelled) setSync("offline");
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws, syncNonce]);

  // Push local changes to the cloud (debounced), with conflict pull. Viewers
  // never push (read-only).
  useEffect(() => {
    if (!ws || readOnly) return;
    const s = JSON.stringify(data);
    if (s === lastSynced.current) return;
    setSync("syncing");
    const t = setTimeout(async () => {
      try {
        const res = await putRemote(ws, data, versionRef.current);
        if ("conflict" in res) {
          versionRef.current = res.version;
          lastSynced.current = JSON.stringify(res.data);
          setData(res.data);
        } else {
          versionRef.current = res.version;
          lastSynced.current = s;
        }
        setSync("synced");
      } catch (e: any) {
        if (e?.readonly) { setReadOnly(true); setSync("synced"); } else setSync("offline");
      }
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, ws, readOnly]);

  function enableSync() {
    if (!user) {
      const pc = window.prompt("Set a passcode to protect this ledger (optional — leave blank for none), or Cancel and Sign in for per-user access:", "");
      if (pc === null) { setAuthPrompt("Sign in to create a private, per-user ledger you control who can access."); return; }
      setPass(pc);
    }
    setWs(ensureWorkspace());
    setSync("syncing");
  }
  function onSignedIn(email: string) {
    setUser(email);
    setAuthPrompt(null);
    setSyncNonce((n) => n + 1); // re-pull now that we're authenticated
  }
  async function doLogout() {
    await logout();
    setUser(null);
    setMembers([]);
    setIsOwner(false);
    setSyncNonce((n) => n + 1);
  }
  async function inviteMember() {
    if (!ws) return;
    const email = window.prompt("Invite a team member by email — they'll get access after signing in with that email:");
    if (!email) return;
    const viewer = window.confirm("Read-only (viewer)?\n\nOK = view only (can't edit)\nCancel = can edit");
    try {
      const r = await setMember(ws, email.trim(), { role: viewer ? "viewer" : "editor" });
      setMembers(r.members);
    } catch (e: any) { alert(String(e.message).slice(0, 120)); }
  }
  async function removeMember(email: string) {
    if (!ws) return;
    try {
      const r = await setMember(ws, email, { remove: true });
      setMembers(r.members);
    } catch (e: any) { alert(String(e.message).slice(0, 120)); }
  }
  async function changePasscode() {
    if (!ws) return;
    const pc = window.prompt("Set a new passcode (leave blank to remove protection):", "");
    if (pc === null) return;
    try {
      setSync("syncing");
      const res = await putRemote(ws, data, versionRef.current, { setPasscode: pc });
      if ("version" in res) versionRef.current = res.version;
      setPass(pc);
      setProtectedLedger(!!pc);
      setSync("synced");
    } catch { setSync("offline"); }
  }
  function copyLink() {
    if (!ws) return;
    navigator.clipboard?.writeText(workspaceLink(ws)).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  }

  // Arriving via "Track payments →" auto-opens (or creates) the linked ledger.
  useEffect(() => {
    if (autoLink) fromContract();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const project = data.projects.find((p) => p.id === selId) || null;

  // Pull any receipts the payee has confirmed on shared certificates when a
  // project opens, so a confirmation made on the link lands in the ledger.
  useEffect(() => {
    if (!project) return;
    for (const c of project.contracts) for (const ct of c.certificates) {
      if (ct.shareId && ct.sentForReceipt && !ct.receipt?.confirmedBy) pullReceipt(c.id, ct);
      if (ct.shareId && !ct.invoice) pullInvoice(c.id, ct);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selId]);

  // While the share modal is open, poll the link: a receipt confirmation (when
  // one was requested) and an invoice the contractor attaches both land here.
  useEffect(() => {
    if (!sharing?.id) return;
    const t = setInterval(async () => {
      const live = project?.contracts.find((x) => x.id === sharing.contractId)?.certificates.find((x) => x.id === sharing.cert.id) || sharing.cert;
      if (sharing.forReceipt && !live.receipt?.confirmedBy) await pullReceipt(sharing.contractId, live);
      if (!live.invoice) await pullInvoice(sharing.contractId, live);
    }, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharing?.forReceipt, sharing?.id, project]);

  function mutProject(id: string, fn: (p: typeof project extends null ? never : NonNullable<typeof project>) => NonNullable<typeof project>) {
    setData((d) => ({ ...d, projects: d.projects.map((p) => (p.id === id ? fn(p) : p)) }));
  }
  const patchProject = (id: string, patch: Partial<NonNullable<typeof project>>) => mutProject(id, (p) => ({ ...p, ...patch }));
  // Convert an amount into the project currency. In "static" mode use the project's
  // fixed rates (1 foreign unit = staticRates[ccy] project-currency units); otherwise
  // use live prevailing market rates. Returns null when no rate is available.
  const conv = (amount: number, from: string): number | null => {
    if (!project) return null;
    const f = (from || project.currency).toUpperCase();
    const to = project.currency.toUpperCase();
    if (f === to) return amount;
    if (project.rateMode === "static") {
      const r = project.staticRates?.[f];
      return r && isFinite(r) && r > 0 ? amount * r : null;
    }
    return convert(amount, from, project.currency, fx);
  };
  // --- Atelier sync -----------------------------------------------------------
  // Every change to a certificate on an Atelier-linked project is mirrored over
  // (debounced per certificate), so the cockpit and client portal stay current.
  useEffect(() => {
    if (!user) { setAtelierProjects(null); return; }
    listAtelierProjects().then((r) => setAtelierProjects(r.configured ? r.projects : null)).catch(() => setAtelierProjects(null));
  }, [user]);
  function syncToAtelier(contractId: string, cert: PaymentCert) {
    if (!project?.atelierProjectId) return;
    const contract = project.contracts.find((c) => c.id === contractId);
    if (!contract) return;
    const proj = project;
    clearTimeout(atelierTimers.current[cert.id]);
    atelierTimers.current[cert.id] = setTimeout(async () => {
      try {
        const r = await pushCertificate(proj.atelierProjectId!, certRecordFor(proj, contract, cert));
        setAtelierMsg(`↑ ${cert.number || "Certificate"} synced to Atelier${r.projectName ? ` · ${r.projectName}` : ""}`);
      } catch (e: any) {
        setAtelierMsg(`Atelier sync failed: ${String(e?.message || e).slice(0, 100)}`);
      }
      setTimeout(() => setAtelierMsg(null), 5000);
    }, 600);
  }
  async function syncAllToAtelier() {
    if (!project?.atelierProjectId) return;
    let n = 0, failed = 0;
    for (const c of project.contracts) for (const ct of c.certificates) {
      try { await pushCertificate(project.atelierProjectId, certRecordFor(project, c, ct)); n++; } catch { failed++; }
    }
    setAtelierMsg(`↑ ${n} certificate${n === 1 ? "" : "s"} synced to Atelier${failed ? ` · ${failed} failed` : ""}`);
    setTimeout(() => setAtelierMsg(null), 6000);
  }
  const patchContract = (pid: string, cid: string, patch: Partial<Contract>) => mutProject(pid, (p) => ({ ...p, contracts: p.contracts.map((c) => (c.id === cid ? { ...c, ...patch } : c)) }));
  const deleteContract = (pid: string, cid: string) => mutProject(pid, (p) => ({ ...p, contracts: p.contracts.filter((c) => c.id !== cid) }));
  const upsertCert = (pid: string, cid: string, cert: PaymentCert) =>
    mutProject(pid, (p) => ({
      ...p,
      contracts: p.contracts.map((c) =>
        c.id === cid ? { ...c, certificates: c.certificates.some((x) => x.id === cert.id) ? c.certificates.map((x) => (x.id === cert.id ? cert : x)) : [...c.certificates, cert] } : c
      ),
    }));
  const deleteCert = (pid: string, cid: string, certId: string) => mutProject(pid, (p) => ({ ...p, contracts: p.contracts.map((c) => (c.id === cid ? { ...c, certificates: c.certificates.filter((x) => x.id !== certId) } : c)) }));

  // --- Digital approval signature (stored in the ledger data) --------------
  const signature = data.signature || null;
  function onSignatureFile(file: File | null | undefined) {
    if (!file) return;
    if (!/^image\//.test(file.type)) { window.alert("Please choose an image file (PNG or JPEG) of your signature."); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const uri = String(reader.result || "");
      if (!/^data:image\/(png|jpe?g);base64,/i.test(uri)) { window.alert("Unsupported image — use a PNG or JPEG."); return; }
      // Validate real dimensions: a degenerate (e.g. 1×1) image can hang the PDF renderer.
      const img = new Image();
      img.onload = () => {
        if (img.naturalWidth < 20 || img.naturalHeight < 8) { window.alert("That image is too small to be a signature — please use a clear scan/photo of your signature."); return; }
        // Tight-crop to the signature strokes so it fills the line (avoids a tiny
        // mark floating in a large whitespace margin).
        const cropped = cropSignature(img) || uri;
        setData((d) => ({ ...d, signature: cropped }));
      };
      img.onerror = () => window.alert("Couldn't read that image — try a different PNG or JPEG.");
      img.src = uri;
    };
    reader.readAsDataURL(file);
    if (sigRef.current) sigRef.current.value = "";
  }
  function clearSignature() {
    setData((d) => ({ ...d, signature: undefined }));
  }
  /** Approve & sign a certificate: stamp the stored signature, approver and date. */
  function approveSign(cid: string, cert: PaymentCert, proj: PaymentProject) {
    if (!signature) { sigRef.current?.click(); return; }
    upsertCert(proj.id, cid, { ...cert, status: "authorised", authorisedSignature: signature, authorisedBy: proj.authoriser || cert.authorisedBy, authorisedDate: nowDate() });
  }
  function unsign(cid: string, cert: PaymentCert, proj: PaymentProject) {
    upsertCert(proj.id, cid, { ...cert, authorisedSignature: undefined, authorisedDate: undefined });
  }

  function addProject(example = false) {
    const p = example ? exampleProject() : newProject(window.prompt("New project name:", "New Project") || "New Project");
    setData((d) => ({ projects: [...d.projects, p] }));
    setSelId(p.id);
  }
  function fromContract() {
    if (!contractSeed?.projectName) return;
    // Select an existing linked project if one already matches, else create it.
    const match = data.projects.find(
      (p) => p.linkedContract && ((contractSeed.reference && p.linkedContract.reference === contractSeed.reference) || p.linkedContract.name === contractSeed.projectName)
    );
    if (match) {
      setSelId(match.id);
      return;
    }
    const p = projectFromContract(contractSeed);
    setData((d) => ({ projects: [...d.projects, p] }));
    setSelId(p.id);
  }
  // Add a new contract, or save edits to an existing one. Changing a contract's
  // currency offers to carry its certificates across too (they usually share it).
  function saveContractForm(pid: string, c: Contract) {
    const normalised = { ...c, currency: (c.currency || "").trim().toUpperCase() || project?.currency || "USD" };
    if (!editingContractId) {
      mutProject(pid, (p) => ({ ...p, contracts: [...p.contracts, normalised] }));
    } else {
      const live = project?.contracts.find((x) => x.id === editingContractId);
      const prev = live?.currency || project?.currency || "";
      let certs = live?.certificates || [];
      if (live && prev && normalised.currency !== prev) {
        const n = certs.filter((ct) => (ct.currency || prev) === prev).length;
        if (n && window.confirm(`Also change the ${n} certificate${n === 1 ? "" : "s"} currently in ${prev} to ${normalised.currency}?\n\nOK = change them too\nCancel = leave them in ${prev}`)) {
          certs = certs.map((ct) => ((ct.currency || prev) === prev ? { ...ct, currency: normalised.currency } : ct));
        }
      }
      patchContract(pid, editingContractId, { ...normalised, certificates: certs });
    }
    setAddingContract(null);
    setEditingContractId(null);
  }
  // Change the project's reporting currency; offer to carry contracts and
  // certificates that are in the old currency across with it.
  function changeProjectCurrency(raw: string) {
    if (!project) return;
    const next = raw.trim().toUpperCase();
    const prev = project.currency;
    if (!next || next === prev) return;
    const nContracts = project.contracts.filter((c) => (c.currency || prev) === prev).length;
    const nCerts = project.contracts.flatMap((c) => c.certificates).filter((ct) => (ct.currency || prev) === prev).length;
    const cascade = (nContracts > 0 || nCerts > 0) && window.confirm(`Change the ${nContracts} contract${nContracts === 1 ? "" : "s"} and ${nCerts} certificate${nCerts === 1 ? "" : "s"} currently in ${prev} to ${next} as well?\n\nOK = change them too (same figures, relabelled)\nCancel = keep them in ${prev}, converted to ${next} at the exchange rate`);
    mutProject(project.id, (p) => ({
      ...p,
      currency: next,
      contracts: cascade
        ? p.contracts.map((c) => ({ ...c, currency: (c.currency || prev) === prev ? next : c.currency, certificates: c.certificates.map((ct) => ((ct.currency || prev) === prev ? { ...ct, currency: next } : ct)) }))
        : p.contracts,
    }));
  }
  const renderContractForm = (c: Contract) => (
    <div className="card contract-form">
      <div className="glabel" style={{ marginBottom: 8 }}>{editingContractId ? "Edit contract" : "New contract"}</div>
      <div className="row-3">
        <label><span className="lbl">Contract name</span><input autoFocus value={c.name} onChange={(e) => setAddingContract({ ...c, name: e.target.value })} placeholder="Glazing Supply" /></label>
        <label><span className="lbl">Kind</span>
          <select value={c.kind} onChange={(e) => setAddingContract({ ...c, kind: e.target.value as ContractKind })}>
            {(["prime", "subcontract", "supply", "consultant", "other"] as ContractKind[]).map((k) => <option key={k} value={k}>{CONTRACT_KIND_LABEL[k]}</option>)}
          </select>
        </label>
        <label><span className="lbl">Payee / party</span><input value={c.party} onChange={(e) => setAddingContract({ ...c, party: e.target.value })} /></label>
      </div>
      <div className="row-3">
        <label><span className="lbl">Supplier (for tracking)</span><input value={c.supplier || ""} onChange={(e) => setAddingContract({ ...c, supplier: e.target.value })} placeholder="defaults to payee" /></label>
        <label><span className="lbl">Contract reference</span><input value={c.reference || ""} onChange={(e) => setAddingContract({ ...c, reference: e.target.value })} /></label>
        <label><span className="lbl">Contract value</span><input type="number" value={numStr(c.value)} onChange={(e) => setAddingContract({ ...c, value: num(e.target.value) })} /></label>
      </div>
      <div className="row-3">
        <label><span className="lbl">Currency</span><input list="ccy-list" value={c.currency} onChange={(e) => setAddingContract({ ...c, currency: e.target.value.toUpperCase() })} placeholder={project?.currency} /></label>
        <label><span className="lbl">Retention %</span><input type="number" value={numStr(c.retentionPercent)} onChange={(e) => setAddingContract({ ...c, retentionPercent: num(e.target.value) })} placeholder="0" /></label>
        <label><span className="lbl">Payee email</span><input value={c.email || ""} onChange={(e) => setAddingContract({ ...c, email: e.target.value })} placeholder="for emailing certificates" /></label>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
        <button className="dark sm" disabled={!c.name.trim()} onClick={() => project && saveContractForm(project.id, c)}>{editingContractId ? "Save contract" : "Add contract"}</button>
        <button className="link" onClick={() => { setAddingContract(null); setEditingContractId(null); }}>Cancel</button>
      </div>
    </div>
  );
  async function doImport(file?: File | null) {
    if (!file) return;
    try {
      const d = await importPayments(file);
      setData(d);
      setSelId(bestProjectId(d.projects));
    } catch (e: any) {
      alert(`Import failed: ${e.message}`);
    }
    if (importRef.current) importRef.current.value = "";
  }
  async function gen(kind: "docx" | "pdf", contract: Contract, cert: PaymentCert) {
    if (!project) return;
    const c = assembleCertificate(project, contract, cert);
    if (kind === "pdf") await exportPdf(c, "A4");
    else await generateDocx(c, "A4");
  }

  // Share a payment certificate as a live link the payee can open (WhatsApp/email).
  // The link id is stored on the cert, so re-sharing refreshes the same link.
  async function shareCert(contract: Contract, cert: PaymentCert) {
    if (!project) return;
    setSharing({ contractId: contract.id, cert, id: cert.shareId || "", busy: true });
    setShareErr(null);
    try {
      const doc = assembleCertificate(project, contract, cert);
      const title = `${project.name} — ${cert.number || "Payment Certificate"}`;
      let id = cert.shareId;
      if (id) await pushSnapshot(id, doc, title);
      else {
        id = (await createReview({ contract: doc, comments: [], title, ref: cert.number, createdAt: new Date().toISOString() })).id;
        upsertCert(project.id, contract.id, { ...cert, shareId: id });
      }
      await syncInvoiceToLink(id, cert);
      syncToAtelier(contract.id, { ...cert, shareId: id });
      setSharing({ contractId: contract.id, cert: { ...cert, shareId: id }, id, busy: false });
    } catch (e: any) {
      setShareErr(`Could not create the share link (${String(e?.message || e).slice(0, 80)}).`);
      setSharing((s) => (s ? { ...s, busy: false } : s));
    }
  }
  // Send a SIGNED certificate to the payee to confirm receipt of the payment —
  // shares the link with the receipt request turned on and marks the cert.
  async function sendForReceipt(contract: Contract, cert: PaymentCert) {
    if (!project) return;
    setSharing({ contractId: contract.id, cert, id: cert.shareId || "", busy: true, forReceipt: true });
    setShareErr(null);
    try {
      const doc = assembleCertificate(project, contract, cert);
      const title = `${project.name} — ${cert.number || "Payment Certificate"}`;
      let id = cert.shareId;
      if (id) await setRequestReceipt(id, true, doc, title);
      else id = (await createReview({ contract: doc, comments: [], title, ref: cert.number, createdAt: new Date().toISOString(), requestReceipt: true })).id;
      await syncInvoiceToLink(id, cert);
      const next = { ...cert, shareId: id, sentForReceipt: true };
      upsertCert(project.id, contract.id, next);
      syncToAtelier(contract.id, next);
      setSharing({ contractId: contract.id, cert: next, id, busy: false, forReceipt: true });
    } catch (e: any) {
      setShareErr(`Could not send for receipt (${String(e?.message || e).slice(0, 80)}).`);
      setSharing((s) => (s ? { ...s, busy: false } : s));
    }
  }

  // Pull a submitted receipt back from the share link into the ledger. Called for
  // certs awaiting receipt (on project open, and while the receipt modal is open).
  async function pullReceipt(contractId: string, cert: PaymentCert): Promise<boolean> {
    if (!cert.shareId) return false;
    try {
      const r = await getReview(cert.shareId);
      if (r.receipt && r.receipt.confirmedBy && r.receipt.at !== cert.receipt?.at) {
        upsertCert(project!.id, contractId, { ...cert, receipt: { confirmedBy: r.receipt.confirmedBy, signature: r.receipt.signature, confirmedDate: r.receipt.confirmedDate, receiptPhoto: r.receipt.receiptPhoto, note: r.receipt.note, at: r.receipt.at } });
        return true;
      }
    } catch { /* offline — try again next time */ }
    return false;
  }
  // The invoice travels with the share link: the issuer's attached invoice is
  // put on the link when sharing, and an invoice the contractor uploads on the
  // link is pulled back onto the certificate. Either way the client's link
  // shows the certificate and the invoice together.
  async function syncInvoiceToLink(linkId: string, cert: PaymentCert) {
    if (!cert.invoice) return;
    try {
      const r = await getReview(linkId);
      if (!(r.attachments || []).some((a) => a.id === cert.invoice!.id)) {
        await attachToReview(linkId, { id: cert.invoice.id, name: cert.invoice.name, size: cert.invoice.size, at: cert.invoice.uploadedAt || new Date().toISOString(), note: "Invoice" });
      }
    } catch { /* link offline — the next share retries */ }
  }
  async function pullInvoice(contractId: string, cert: PaymentCert): Promise<boolean> {
    if (!cert.shareId || cert.invoice) return false;
    try {
      const r = await getReview(cert.shareId);
      const a = (r.attachments || [])[0]; // newest first
      if (a) {
        const next = { ...cert, invoice: { id: a.id, name: a.name, size: a.size, uploadedAt: a.at } };
        upsertCert(project!.id, contractId, next);
        syncToAtelier(contractId, next);
        return true;
      }
    } catch { /* offline — try again next time */ }
    return false;
  }

  async function genStatement(kind: "docx" | "pdf", supplier: string) {
    if (!project) return;
    const c = assembleSupplierStatement(project, supplier);
    if (kind === "pdf") await exportPdf(c, "A4");
    else await generateDocx(c, "A4");
  }

  // Scan a supplier quotation/invoice PDF → draft a payment certificate.
  async function scanQuote(file?: File | null) {
    if (!file || !project) return;
    setScanning(true);
    setScanMsg("Reading document…");
    try {
      const results = await extractManyPdfs([file]);
      const text = results.map((r) => r.text).join("\n");
      if (!text.trim()) throw new Error("No selectable text found (scanned image?).");
      setScanMsg("Extracting details…");
      const res = await fetch("/api/extract-quote", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text }) });
      if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
      const { quote } = (await res.json()) as { quote?: { supplier?: string; reference?: string; contractReference?: string; date?: string; description?: string; amount?: number; currency?: string; kind?: CertKind; paymentType?: string; totalContractValue?: number; depositPercent?: number } };
      if (!quote) throw new Error("No fields returned.");

      const numOr = (v: any) => (v != null && isFinite(Number(v)) ? Number(v) : undefined);
      const supplierName = (quote.supplier || "").trim() || "Supplier";
      const currency = (quote.currency || project.currency).trim();
      const dueNow = numOr(quote.amount);                          // deposit / this payment
      const total = numOr(quote.totalContractValue) ?? dueNow;     // overall scope value → contract value
      const scope = (quote.description || "").trim();
      const contractRef = (quote.contractReference || quote.reference || "").trim();
      const certKind: CertKind = quote.kind === "construction" || quote.kind === "services" ? quote.kind : "material-supply";
      const pt = quote.paymentType || "progress";

      // Match an existing contract by supplier/party, else create one seeded from
      // the document — its VALUE is the overall (100%) contract sum.
      let contract = project.contracts.find((c) => (c.supplier || c.party || "").trim().toLowerCase() === supplierName.toLowerCase());
      let contractId: string;
      if (contract) {
        contractId = contract.id;
        // Backfill the contract value from the overall sum if it wasn't set.
        if ((contract.value == null || !isFinite(contract.value)) && total != null) patchContract(project.id, contract.id, { value: total });
      } else {
        const kind: ContractKind = certKind === "construction" ? "subcontract" : certKind === "services" ? "consultant" : "supply";
        const nc: Contract = { ...newContract(currency), name: scope ? `${scope} — ${supplierName}`.slice(0, 90) : `${supplierName} — Supply`, kind, party: supplierName, supplier: supplierName, reference: contractRef || undefined, value: total, currency };
        contractId = nc.id;
        mutProject(project.id, (p) => ({ ...p, contracts: [...p.contracts, nc] }));
        contract = nc;
      }
      // Certificate is for the amount DUE NOW (the deposit), labelled accordingly.
      const isPartial = total != null && dueNow != null && total > dueNow + 0.5;
      let desc = scope;
      if (pt === "advance") desc = `Advance Payment — ${scope}`;
      else if (pt === "deposit" || isPartial) {
        const pctTxt = quote.depositPercent ? ` (${quote.depositPercent}%)` : isPartial ? ` (${Math.round((dueNow! / total!) * 100)}%)` : "";
        desc = `Deposit${pctTxt}${isPartial ? ` of ${currency} ${total!.toLocaleString()}` : ""} — ${scope}`;
      }
      const seq = `${(contract.certificates.length || 0) + 1}`.padStart(3, "0");
      // The scanned document IS the invoice — keep it with the certificate so it
      // goes out to the client with it. Best-effort: a failed upload just means
      // attaching it by hand in the editor.
      const stored = await uploadFile(file).catch(() => undefined);
      const cert: PaymentCert = {
        ...newCert(currency, contractRef || seq),
        kind: certKind,
        description: desc,
        invoiceRef: quote.reference || contractRef || "",
        invoiceDate: quote.date || "",
        amount: dueNow ?? NaN,
        currency,
        date: quote.date || nowDate(),
        invoice: stored,
      };
      setEditing({ contractId, cert });
      setScanMsg(total != null && dueNow != null && total > dueNow ? `Contract value ${currency} ${total.toLocaleString()}; certifying the ${pt} of ${currency} ${dueNow.toLocaleString()}.` : null);
      setTimeout(() => setScanMsg(null), 6000);
    } catch (e: any) {
      setScanMsg(`Scan failed: ${String(e.message).slice(0, 100)}`);
    } finally {
      setScanning(false);
      if (quoteRef.current) quoteRef.current.value = "";
    }
  }

  // Scan an invoice / request and add a certificate UNDER a specific contract, so
  // all payments, advances and deposits are ledgered against that contract. Kind
  // follows the contract (supply → non-FIDIC material-supply certification; prime/
  // sub → construction FIDIC); advances/deposits are labelled (and construction
  // advances flagged repayable via the description so isAdvanceCert catches them).
  async function scanIntoContract(contractId: string, file?: File | null) {
    if (!file || !project) return;
    const contract = project.contracts.find((c) => c.id === contractId);
    if (!contract) return;
    setScanning(true);
    setScanMsg("Reading document…");
    try {
      const results = await extractManyPdfs([file]);
      const text = results.map((r) => r.text).join("\n");
      if (!text.trim()) throw new Error("No selectable text found (scanned image?).");
      setScanMsg("Extracting payment…");
      const res = await fetch("/api/extract-quote", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text }) });
      if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
      const { quote } = (await res.json()) as { quote?: { reference?: string; date?: string; description?: string; amount?: number; currency?: string; kind?: CertKind; paymentType?: string; totalContractValue?: number; depositPercent?: number } };
      if (!quote) throw new Error("No fields returned.");
      const numOr = (v: any) => (v != null && isFinite(Number(v)) ? Number(v) : undefined);
      const currency = (quote.currency || contract.currency || project.currency).trim();
      const dueNow = numOr(quote.amount);
      const total = numOr(quote.totalContractValue);
      // Backfill this contract's overall value from the document if it's not set.
      if (total != null && (contract.value == null || !isFinite(contract.value))) patchContract(project.id, contract.id, { value: total });
      const certKind: CertKind = contract.kind === "supply" ? "material-supply" : contract.kind === "consultant" ? "services" : "construction";
      const pt = quote.paymentType || "progress";
      let desc = quote.description || "";
      if (pt === "advance") desc = `Advance Payment — ${desc}`;
      else if (pt === "deposit") desc = `Deposit${quote.depositPercent ? ` (${quote.depositPercent}%)` : ""}${total != null && dueNow != null && total > dueNow ? ` of ${currency} ${total.toLocaleString()}` : ""} — ${desc}`;
      const seq = `${(contract.certificates.length || 0) + 1}`.padStart(3, "0");
      // The scanned document IS the invoice — keep it with the certificate so it
      // goes out to the client with it. Best-effort: a failed upload just means
      // attaching it by hand in the editor.
      const stored = await uploadFile(file).catch(() => undefined);
      const cert: PaymentCert = {
        ...newCert(currency, quote.reference || seq),
        kind: certKind,
        description: desc,
        invoiceRef: certKind === "material-supply" ? quote.reference || "" : undefined,
        invoiceDate: quote.date || "",
        amount: dueNow ?? NaN,
        currency,
        date: quote.date || nowDate(),
        invoice: stored,
      };
      setEditing({ contractId, cert });
      setScanMsg(total != null && dueNow != null && total > dueNow ? `Contract value set to ${currency} ${total.toLocaleString()}; certifying the ${pt} of ${currency} ${dueNow.toLocaleString()}.` : null);
      if (total != null && dueNow != null && total > dueNow) setTimeout(() => setScanMsg(null), 6000);
    } catch (e: any) {
      setScanMsg(`Scan failed: ${String(e.message).slice(0, 100)}`);
    } finally {
      setScanning(false);
      setScanContractId(null);
      if (contractScanRef.current) contractScanRef.current.value = "";
    }
  }

  const suppliers = useMemo(() => (project ? bySupplier(project) : []), [project]);
  const shownContracts = useMemo(() => {
    if (!project) return [];
    if (!supplierFilter) return project.contracts;
    return project.contracts.filter((c) => (c.supplier || c.party || "—") === supplierFilter);
  }, [project, supplierFilter]);

  return (
    <div className="pay">
      <datalist id="ccy-list">{CURRENCIES.map((x) => <option key={x} value={x} />)}</datalist>
      <div className="pay-toolbar">
        <select value={selId ?? ""} onChange={(e) => setSelId(e.target.value || null)}>
          <option value="">{data.projects.length ? "Select project…" : "No projects yet"}</option>
          {data.projects.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        <button className="dark sm" onClick={() => addProject(false)}>+ Project</button>
        {contractSeed?.projectName && <button className="sm" onClick={fromContract} title={`Link a payment ledger to the contract "${contractSeed.projectName}"`}>⛓ From contract</button>}
        <button className="sm" onClick={() => addProject(true)}>Example</button>
        {project && (
          <button className="sm" onClick={() => quoteRef.current?.click()} disabled={scanning} title="Scan a supplier quotation or invoice — matches or CREATES a contract from it, then drafts the first certificate">
            {scanning && !scanContractId ? <span className="spinner dark-spin" /> : "⤒ New contract from invoice"}
          </button>
        )}
        <input ref={quoteRef} type="file" accept="application/pdf" style={{ display: "none" }} onChange={(e) => scanQuote(e.target.files?.[0])} />
        <input ref={contractScanRef} type="file" accept="application/pdf" style={{ display: "none" }} onChange={(e) => { if (scanContractId) scanIntoContract(scanContractId, e.target.files?.[0]); }} />
        <div style={{ flex: 1 }} />
        {scanMsg && <span className="hint" style={{ margin: 0, color: scanMsg.startsWith("Scan failed") ? "#a3402f" : "var(--muted)" }}>{scanMsg}</span>}
        {user ? (
          <span className="acct" title={`Signed in as ${user}`}>👤 {user}<button className="link" onClick={doLogout} style={{ marginLeft: 6 }}>Sign out</button></span>
        ) : (
          <button className="link" onClick={() => setAuthPrompt("Sign in for a private, per-user ledger you control access to.")}>Sign in</button>
        )}
        {ws ? (
          <span className={`sync-chip s-${sync}`} title={`Cloud sync — ${sync}`}>
            <span className="sync-dot" />{sync === "synced" ? "Synced" : sync === "syncing" ? "Saving…" : sync === "offline" ? "Offline" : "Local"}
          </span>
        ) : (
          <button className="link" onClick={enableSync} title="Store the ledger in the cloud so it survives, syncs across devices, and can be shared">☁ Enable sync</button>
        )}
        <button className="link" onClick={() => exportPayments(data)}>Export</button>
        <button className="link" onClick={() => importRef.current?.click()}>Import</button>
        <input ref={importRef} type="file" accept="application/json,.json" style={{ display: "none" }} onChange={(e) => doImport(e.target.files?.[0])} />
        <span className="sig-set">
          {signature ? (
            <>
              <img src={signature} alt="Approval signature" className="sig-thumb" style={{ height: 22, verticalAlign: "middle", background: "#fff", border: "1px solid #e5e3dc", borderRadius: 3, padding: "1px 4px" }} />
              <button className="link" onClick={() => sigRef.current?.click()} title="Replace your approval signature">Change signature</button>
              <button className="link" onClick={clearSignature}>Remove</button>
            </>
          ) : (
            <button className="link" onClick={() => sigRef.current?.click()} title="Upload an image of your signature to use for approvals">✎ Set approval signature</button>
          )}
        </span>
        <input ref={sigRef} type="file" accept="image/png,image/jpeg" style={{ display: "none" }} onChange={(e) => onSignatureFile(e.target.files?.[0])} />
      </div>

      {ws && (
        <div className="share-box" style={{ marginBottom: 16 }}>
          <div className="lbl">
            Ledger sync link — open on any device or share with your team to view/edit the same ledger.
            {protectedLedger ? <span className="lock-badge on"> 🔒 Passcode protected</span> : <span className="lock-badge"> 🔓 No passcode</span>}
          </div>
          <div className="share-row">
            <input readOnly value={workspaceLink(ws)} onFocus={(e) => e.currentTarget.select()} />
            <button onClick={copyLink}>{copied ? "Copied ✓" : "Copy"}</button>
            <button onClick={changePasscode} title="Set or change the passcode">{protectedLedger ? "Change passcode" : "Set passcode"}</button>
            <ShareButtons url={workspaceLink(ws)} title="Payments ledger" />
          </div>
          {protectedLedger && <div className="hint" style={{ margin: "6px 0 0" }}>Share the passcode with your team separately from the link — anyone opening the link will be asked for it.</div>}
          {isOwner && members.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div className="lbl">Access — members can open this ledger by signing in with their email</div>
              <div className="member-list">
                {members.map((m) => (
                  <span className="member-chip" key={m.email}>
                    {m.email} <span className="mrole">· {m.role === "owner" ? "owner" : m.role === "viewer" ? "viewer" : "editor"}</span>
                    {m.role !== "owner" && <button className="chip-x" onClick={() => removeMember(m.email)} title="Remove">✕</button>}
                  </span>
                ))}
              </div>
              <button className="link" onClick={inviteMember} style={{ marginTop: 4 }}>+ Invite by email</button>
            </div>
          )}
        </div>
      )}
      {accessMsg && <div className="notice" style={{ color: "#a3402f" }}>{accessMsg}</div>}
      {readOnly && <div className="notice" style={{ color: "#2f5fa3" }}>🔒 Read-only access — you can view this ledger but not change it. Ask the owner for editor access to make changes.</div>}

      {!project ? (
        <div className="pay-empty">
          <p>Track every authorised payment for a project — across the prime contract and any subcontracts, supply agreements or consultants — with the payee's acknowledgement of settlement to date.</p>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="dark" onClick={() => addProject(false)}>Create a project</button>
            <button onClick={() => addProject(true)}>Load an example</button>
          </div>
        </div>
      ) : (
        <div className="pay-body" style={readOnly ? { pointerEvents: "none", opacity: 0.9 } : undefined}>
          {/* Project header. The project's settings (reference, authoriser,
              exchange rate, budget) live in a collapsed panel at the bottom —
              the daily job here is raising certificates, so contracts come first. */}
          <div className="pay-head">
            {project.linkedContract && (
              <div className="link-chip">⛓ Linked to contract: <b>{project.linkedContract.name}</b>{project.linkedContract.reference ? ` (${project.linkedContract.reference})` : ""}</div>
            )}
            {project.atelierProjectId ? (
              <div className="link-chip" title="Certificates sync to this Atelier project (studio cockpit + client portal) as they are saved, shared or emailed">◈ Atelier: <b>{project.atelierProjectName || atelierProjects?.find((p) => p.id === project.atelierProjectId)?.name || project.atelierProjectId}</b>{atelierMsg ? <span className="muted"> · {atelierMsg}</span> : null}</div>
            ) : atelierProjects && atelierProjects.length > 0 ? (
              <div className="link-chip" style={{ cursor: "pointer" }} onClick={() => document.querySelector<HTMLDetailsElement>(".pay-settings")?.setAttribute("open", "")} title="Pick the Atelier project in Project settings">◈ Not linked to an Atelier project — link it in Project settings to share certificates from the client portal</div>
            ) : null}
            <div className="wb-k" style={{ marginBottom: 2 }}>Ledger project · {project.contracts.length} contract{project.contracts.length === 1 ? "" : "s"} · {projectCertCount(project)} certificate{projectCertCount(project) === 1 ? "" : "s"}</div>
            <div className="pay-title-row">
              <input className="pay-title" value={project.name} onChange={(e) => patchProject(project.id, { name: e.target.value })} />
              <CurrencyField value={project.currency} onCommit={changeProjectCurrency} title="The currency this project's totals are reported in. Contracts and certificates can be in other currencies — they convert at the exchange rate in Project settings." />
            </div>
          </div>

          {/* Summary */}
          {(() => {
            const certified = projectCertifiedIn(project, project.currency, conv);
            const paid = projectPaidIn(project, project.currency, conv);
            const outstanding = Math.max(0, certified.total - paid.total);
            const budget = project.budget != null && isFinite(project.budget) ? project.budget : null;
            const pct = budget && budget > 0 ? (certified.total / budget) * 100 : null;
            const remaining = budget != null ? budget - certified.total : null;
            return (
              <>
                <div className="pay-cards">
                  <div className="pay-card">
                    <div className="pc-n">{fmt(certified.total, project.currency)}</div>
                    <div className="pc-l">Total authorised{certified.hasForeign ? ` (in ${project.currency})` : ""}</div>
                  </div>
                  <div className="pay-card">
                    <div className="pc-n">{fmt(paid.total, project.currency)}</div>
                    <div className="pc-l">Total paid{outstanding > 0.5 ? ` · ${fmt(outstanding, project.currency)} outstanding` : ""}</div>
                  </div>
                  {budget != null && (
                    <div className="pay-card">
                      <div className="pc-n">{pct != null ? `${pct.toFixed(1)}%` : "—"}</div>
                      <div className="pc-l">of budget {fmt(budget, project.currency)}{remaining != null ? ` · ${remaining < 0 ? "over by" : "remaining"} ${fmt(Math.abs(remaining), project.currency)}` : ""}</div>
                      {pct != null && <div className="pos-bar" style={{ marginTop: 6 }}><span style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} className={pct > 100 ? "over" : ""} /></div>}
                    </div>
                  )}
                  <div className="pay-card"><div className="pc-n">{projectCertCount(project)}</div><div className="pc-l">Certificates</div></div>
                  <div className="pay-card"><div className="pc-n">{project.contracts.length}</div><div className="pc-l">Contracts</div></div>
                </div>
                {certified.hasForeign && (
                  <div className="hint" style={{ margin: "-10px 0 16px" }}>
                    {project.rateMode === "static"
                      ? <>Foreign-currency amounts converted to {project.currency} at this project's <b>fixed rates</b>.</>
                      : fx ? <>Foreign-currency contracts converted to {project.currency} at live rates{fx.date ? ` (${fx.date})` : ""}.</> : <>Foreign-currency contracts present — live rates unavailable, so those amounts aren’t converted yet.</>}
                    {certified.unconverted > 0 && <> {fmt(certified.unconverted, "")} in {project.rateMode === "static" ? "currencies without a fixed rate" : "unconvertible currencies"} is excluded.</>}
                  </div>
                )}
              </>
            );
          })()}

          {/* Contracts + certificates — the primary job of this page */}
          <div className="group">
            <div className="glabel">Contracts &amp; certificates {supplierFilter && <button className="link" onClick={() => setSupplierFilter(null)}>· showing {supplierFilter} — clear</button>}</div>
            {/* Empty project, but another one has certificates — the selector is
                almost certainly on the wrong project. Offer to switch rather than
                leaving a blank section that looks like lost data. */}
            {projectCertCount(project) === 0 && (() => {
              const other = data.projects.find((p) => p.id !== project.id && projectCertCount(p) > 0);
              return other ? (
                <div className="switch-note">
                  This project has no certificates yet.
                  <button className="link" onClick={() => { setSupplierFilter(null); setSelId(other.id); }}>
                    Open “{other.name.trim()}” ({projectCertCount(other)} certificate{projectCertCount(other) === 1 ? "" : "s"}) →
                  </button>
                </div>
              ) : null;
            })()}
            {shownContracts.map((c) => {
              const cur = c.currency || project.currency;
              // One certificate line, plus its Clause 14 detail when it carries a build-up.
              const renderRow = (ct: PaymentCert) => {
                const s = certSeries(ct);
                const bu = ct.buildup;
                const detailed = (s === "interim" || s === "final") && hasValuation(bu);
                const sum = detailed ? computeBuildup(c, ct) : null;
                return (
                  <div className={`cert-item${focus?.certId === ct.id ? " focus" : ""}`} key={ct.id}>
                    <div className="cert-row">
                      <span className="cert-no">{ct.number || "—"}</span>
                      <span className={`cert-kind ck-${s}`}>{SERIES_SHORT[s]}</span>
                      <span className="cert-desc" title={ct.description}>
                        {ct.description || <em className="muted">no description</em>}{ct.invoiceRef && s === "supply" ? ` · inv ${ct.invoiceRef}` : ""}
                        {ct.invoice && <a className="cert-inv" href={fileUrl(ct.invoice.id)} target="_blank" rel="noreferrer" title={`Invoice: ${ct.invoice.name} (${fileSize(ct.invoice.size)})`}>📎 invoice</a>}
                        {(ct.emailLog || []).length > 0 && <span className="cert-sent" title={(ct.emailLog || []).map((e) => `${e.at.slice(0, 10)} → ${e.to.join(", ")}`).join("\n")}>✉ sent ×{(ct.emailLog || []).length}</span>}
                      </span>
                      <span className="cert-amt">{fmt(ct.amount, ct.currency)}</span>
                      <span className={`cert-status s-${ct.status}`}>{CERT_STATUS_LABEL[ct.status]}</span>
                      <span className={`pay-state p-${certPayState(ct)}`} title={certPayState(ct) === "paid" ? "Settled in full" : `${fmt(certPaid(ct), ct.currency)} received · ${fmt(certOutstanding(ct), ct.currency)} outstanding`}>
                        {PAY_STATE_LABEL[certPayState(ct)]}
                        {certPayState(ct) === "partial" && <span className="pay-out"> · {fmt(certOutstanding(ct), ct.currency)} left</span>}
                      </span>
                      <span className="cert-actions">
                        {ct.receipt?.confirmedBy ? (
                          <span className="pay-state p-paid" title={`Receipt confirmed by ${ct.receipt.confirmedBy}${ct.receipt.confirmedDate ? ` on ${ct.receipt.confirmedDate}` : ""}`}>✓ Receipt</span>
                        ) : ct.sentForReceipt ? (
                          <span className="pay-state p-open" title="Sent to the payee to confirm receipt of payment">Awaiting receipt</span>
                        ) : null}
                        {ct.authorisedSignature ? (
                          <button className="mini" style={{ color: "#2f7d4f", borderColor: "#bfe0c9" }} onClick={() => unsign(c.id, ct, project)} title={`Signed${ct.authorisedBy ? ` by ${ct.authorisedBy}` : ""}${ct.authorisedDate ? ` on ${ct.authorisedDate}` : ""} — click to remove signature`}>✓ Signed</button>
                        ) : (
                          <button className="mini dark" onClick={() => approveSign(c.id, ct, project)} title={signature ? "Approve & apply your signature to this certificate" : "Upload your approval signature, then it applies here"}>✓ Approve &amp; sign</button>
                        )}
                        {ct.authorisedSignature && (
                          <button className="mini" onClick={() => sendForReceipt(c, ct)} title={ct.receipt?.confirmedBy ? "View the payee's receipt confirmation" : "Send this signed certificate to the payee to sign for receipt of payment and upload the receipt photo"}>📤 Receipt</button>
                        )}
                        <button className="mini" onClick={() => gen("docx", c, ct)} title="Word">DOC</button>
                        <button className="mini" onClick={() => gen("pdf", c, ct)} title="PDF">PDF</button>
                        <button className="mini" onClick={() => shareCert(c, ct)} title="Share a live link to this certificate by WhatsApp or email">🔗</button>
                        <button className="mini" onClick={() => setEditing({ contractId: c.id, cert: ct })}>Edit</button>
                        <button className="mini" onClick={() => { if (window.confirm(`Delete certificate ${ct.number || ""}?`)) deleteCert(project.id, c.id, ct.id); }}>✕</button>
                      </span>
                    </div>
                    {sum && (
                      <div className="cert-detail">
                        <span>Gross to date <b>{fmt(sum.gross, ct.currency)}</b></span>
                        <span>retention held <b>{fmt(sum.retentionHeld, ct.currency)}</b></span>
                        {sum.advanceRecovered > 0 && <span>advance recovered <b>{fmt(sum.advanceRecovered, ct.currency)}</b></span>}
                        <span>previously certified <b>{fmt(sum.previouslyCertified, ct.currency)}</b></span>
                        <span>due this certificate <b>{fmt(sum.dueThisCert, ct.currency)}</b>{sum.vat ? ` + VAT ${fmt(sum.vat, ct.currency)}` : ""}</span>
                        {(bu?.statementRef || bu?.periodTo) && <span className="muted">{bu?.statementRef ? `Statement ${bu.statementRef}` : ""}{bu?.periodFrom || bu?.periodTo ? ` · ${bu?.periodFrom || "…"} → ${bu?.periodTo || "…"}` : ""}</span>}
                      </div>
                    )}
                    {s === "advance" && bu && (bu.installmentIndex || bu.advancePercent != null || bu.guaranteeRef) && (
                      <div className="cert-detail">
                        {bu.installmentIndex ? <span>Installment <b>{bu.installmentIndex}{bu.installmentCount ? ` of ${bu.installmentCount}` : ""}</b></span> : null}
                        {bu.advancePercent != null && isFinite(bu.advancePercent) ? <span>advance <b>{bu.advancePercent}%</b> of the contract</span> : null}
                        {bu.guaranteeRef ? <span>guarantee <b>{bu.guaranteeRef}</b></span> : null}
                      </div>
                    )}
                  </div>
                );
              };
              // Advances and works certificates are different money: group them so
              // the ledger reads as the contract does.
              const adv = c.certificates.filter((x) => certSeries(x) === "advance");
              const works = c.certificates.filter((x) => { const s = certSeries(x); return s === "interim" || s === "final"; });
              const rest = c.certificates.filter((x) => !adv.includes(x) && !works.includes(x));
              const groups: { key: string; label: string; note?: string; certs: PaymentCert[] }[] = [];
              if (adv.length) groups.push({ key: "adv", label: "Advance payments · Sub-Clause 14.2", note: "repayable — not counted against the contract value", certs: adv });
              if (works.length) groups.push({ key: "works", label: "Interim & final certificates · Sub-Clause 14.6", note: "works certified — retention applies", certs: works });
              if (rest.length) groups.push({ key: "rest", label: adv.length || works.length ? "Other certificates" : "", certs: rest });
              const gsum = (xs: PaymentCert[]) => xs.reduce((s, x) => s + (isFinite(x.amount) ? x.amount : 0), 0);
              return (
              <div className={`pay-contract${focus?.contractId === c.id ? " focus" : ""}`} key={c.id} id={`contract-${c.id}`}>
                <div className="pcx-head">
                  <div>
                    <span className={`kind-badge k-${c.kind}`}>{CONTRACT_KIND_LABEL[c.kind]}</span>
                    <b> {c.name || "(unnamed contract)"}</b> — <span className="muted">{c.party || "payee?"}</span>
                    {(() => {
                      const pos = contractPosition(c);
                      const cur = c.currency || project.currency;
                      if (!c.certificates.length && pos.value == null) return null;
                      return (
                        <div className="pcx-pos">
                          Certified <b>{fmt(pos.certified, cur)}</b>{pos.value != null ? <> of {fmt(pos.value, cur)}{pos.pct != null && <> · {pos.pct.toFixed(1)}%</>}</> : null}
                          {pos.advancePaid > 0 && <> · advance paid <b>{fmt(pos.advancePaid, cur)}</b></>}
                          {pos.retentionHeld > 0 && <> · retention held {fmt(pos.retentionHeld, cur)}</>}
                          {pos.pct != null && <div className="pos-bar"><span style={{ width: `${Math.min(100, Math.max(0, pos.pct))}%` }} className={pos.pct > 100 ? "over" : ""} /></div>}
                        </div>
                      );
                    })()}
                  </div>
                  <div className="pcx-total">{fmt(contractTotal(c), c.currency || project.currency)}
                    {(c.currency || project.currency) !== project.currency && (() => { const t = contractTotalIn(c, project.currency, project.currency, conv); return <div className="muted" style={{ fontSize: 10.5, fontWeight: 400 }}>{t != null ? `≈ ${fmt(t, project.currency)}` : `(no ${project.currency} rate)`}</div>; })()}
                  </div>
                </div>
                <div className="pcx-certs">
                  {c.certificates.length === 0 && <div className="hint">No certificates yet — raise the first one below.</div>}
                  {groups.map((g) => (
                    <div className="cert-group" key={g.key}>
                      {g.label && (
                        <div className="cert-group-head">
                          <span>{g.label}</span>
                          <span className="muted">{g.certs.length} · {fmt(gsum(g.certs), cur)}{g.note ? ` · ${g.note}` : ""}</span>
                        </div>
                      )}
                      {g.certs.map(renderRow)}
                    </div>
                  ))}
                </div>
                <div className="pcx-foot">
                  <NewCertMenu
                    contract={c}
                    scanning={scanning && scanContractId === c.id}
                    onPick={(s) => setEditing({ contractId: c.id, cert: applySeries(newCert(c.currency || project.currency, ""), s, c) })}
                    onScan={() => { setScanContractId(c.id); contractScanRef.current?.click(); }}
                  />
                  <span className="muted" style={{ fontSize: 11.5 }}>
                    Next: {seriesForContract(c).slice(0, 3).map((s) => `${SERIES_SHORT[s]} ${nextCertNumber(c, s)}`).join(" · ")}
                  </span>
                  <div style={{ flex: 1 }} />
                  <button className="link" onClick={() => { setAddingContract({ ...c }); setEditingContractId(c.id); }} title="Name, payee, value, currency, retention">Edit contract</button>
                  <button className="link" onClick={() => { if (window.confirm(`Delete contract "${c.name}" and its certificates?`)) deleteContract(project.id, c.id); }}>Delete contract</button>
                </div>
                {editingContractId === c.id && addingContract && <div style={{ padding: "0 14px 12px" }}>{renderContractForm(addingContract)}</div>}
              </div>
              );
            })}
            {addingContract && !editingContractId ? renderContractForm(addingContract) : (
              <button onClick={() => { setEditingContractId(null); setAddingContract(newContract(project.currency)); }} style={{ marginTop: 6 }}>+ Add contract</button>
            )}
          </div>

          {/* Project settings — collapsed; rarely touched once set up */}
          <details className="pay-settings">
            <summary>
              <span>Project settings</span>
              <span className="ps-sum">{[project.reference, project.location, project.currency, project.authoriser].filter(Boolean).join(" · ") || "reference, authoriser, currency, exchange rate, budget"}</span>
            </summary>
            <div className="ps-body">
            <div className="row-3">
              <label><span className="lbl">Reference</span><input value={project.reference || ""} onChange={(e) => patchProject(project.id, { reference: e.target.value })} /></label>
              <label><span className="lbl">Location</span><input value={project.location || ""} onChange={(e) => patchProject(project.id, { location: e.target.value })} /></label>
              <label><span className="lbl">Currency</span><input value={project.currency} onChange={(e) => patchProject(project.id, { currency: e.target.value })} /></label>
            </div>
            <div className="row">
              <label><span className="lbl">Authoriser (name)</span><input value={project.authoriser || ""} onChange={(e) => patchProject(project.id, { authoriser: e.target.value })} /></label>
              <label><span className="lbl">Authoriser title</span><input value={project.authoriserTitle || ""} onChange={(e) => patchProject(project.id, { authoriserTitle: e.target.value })} /></label>
            </div>
            <label><span className="lbl">Client / Employer email(s) <span className="muted">· prefilled when a certificate is emailed</span></span><input value={project.clientEmail || ""} onChange={(e) => patchProject(project.id, { clientEmail: e.target.value })} placeholder="client@example.com" /></label>
            {/* Atelier link — which cockpit project these certificates mirror to. */}
            <div className="row" style={{ gridTemplateColumns: "1fr auto", alignItems: "end", gap: 12 }}>
              <label style={{ margin: 0 }}><span className="lbl">Atelier project <span className="muted">· certificates sync to its cockpit and client portal</span></span>
                {atelierProjects ? (
                  <select value={project.atelierProjectId || ""} onChange={(e) => { const ap = atelierProjects.find((x) => x.id === e.target.value); patchProject(project.id, { atelierProjectId: ap?.id || undefined, atelierProjectName: ap?.name || undefined, clientEmail: project.clientEmail || ap?.clientEmail || undefined }); }}>
                    <option value="">— not linked —</option>
                    {atelierProjects.map((ap) => <option key={ap.id} value={ap.id}>{ap.code ? `${ap.code} · ` : ""}{ap.name}</option>)}
                  </select>
                ) : (
                  <input value={project.atelierProjectName || project.atelierProjectId || ""} readOnly placeholder={user ? "Atelier sync isn't configured on the server" : "Sign in to link an Atelier project"} style={{ background: "#f4f3ef", color: "var(--muted)" }} />
                )}
              </label>
              {project.atelierProjectId && <button className="sm" onClick={syncAllToAtelier} title="Push every certificate on this project to Atelier now">↑ Sync all now</button>}
            </div>
            <div className="row" style={{ gridTemplateColumns: "auto 1fr", alignItems: "center", gap: 12 }}>
              <label style={{ margin: 0 }}><span className="lbl">Budget ({project.currency})</span>
                <input type="number" value={numStr(project.budget)} onChange={(e) => patchProject(project.id, { budget: num(e.target.value) })} placeholder="e.g. 1000000000" />
              </label>
              <div className="hint" style={{ margin: "18px 0 0" }}>Set a budget to track certified payments against it in the summary above.</div>
            </div>
            {/* Exchange rate: prevailing (live market) vs static (fixed) */}
            <div className="row" style={{ gridTemplateColumns: "auto 1fr", alignItems: "start", gap: 12 }}>
              <label style={{ margin: 0 }}>
                <span className="lbl">Exchange rate</span>
                <select value={project.rateMode || "prevailing"} onChange={(e) => patchProject(project.id, { rateMode: e.target.value as "prevailing" | "static" })}>
                  <option value="prevailing">Prevailing rate (live market)</option>
                  <option value="static">Static rate (fixed)</option>
                </select>
              </label>
              <div>
                {(() => {
                  const foreign = Array.from(new Set(
                    project.contracts.flatMap((c) => [c.currency, ...c.certificates.map((ct) => ct.currency)]),
                  )).map((x) => (x || "").toUpperCase()).filter((x) => x && x !== project.currency.toUpperCase());
                  if ((project.rateMode || "prevailing") !== "static") {
                    return (
                      <div className="hint" style={{ margin: "22px 0 0" }}>
                        {fx ? <>Foreign amounts convert to {project.currency} at live market rates{fx.date ? ` (as of ${fx.date})` : ""}, refreshed automatically.</> : <>Live market rates are currently unavailable.</>}
                      </div>
                    );
                  }
                  if (!foreign.length) {
                    return <div className="hint" style={{ margin: "22px 0 0" }}>Fixed rates apply once a contract or certificate is in another currency. Add one to set its rate.</div>;
                  }
                  return (
                    <div className="static-rates">
                      <div className="lbl" style={{ marginBottom: 5 }}>Fixed rate held for this project</div>
                      {foreign.map((ccy) => (
                        <label key={ccy} className="static-rate-row">
                          <span>1 {ccy} =</span>
                          <input
                            type="number"
                            value={numStr(project.staticRates?.[ccy])}
                            onChange={(e) => patchProject(project.id, { staticRates: { ...(project.staticRates || {}), [ccy]: num(e.target.value) } })}
                            placeholder="rate"
                          />
                          <span>{project.currency}</span>
                        </label>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
            </div>
          </details>

          {/* Financial position */}
          {project.contracts.length > 0 && (
            <div className="group">
              <div className="glabel">Financial position — certified vs contract value</div>
              <table className="pay-table pos-table">
                <thead><tr><th>Contract</th><th style={{ textAlign: "right" }}>Value</th><th>Certified to date</th><th style={{ textAlign: "right" }}>Ret. %</th><th style={{ textAlign: "right" }}>Retention held</th><th style={{ textAlign: "right" }}>Remaining</th></tr></thead>
                <tbody>
                  {project.contracts.map((c) => {
                    const pos = contractPosition(c);
                    const cur = c.currency || project.currency;
                    return (
                      <tr key={c.id}>
                        <td>{c.name || "(unnamed)"}<div className="muted" style={{ fontSize: 10.5 }}>{CONTRACT_KIND_LABEL[c.kind]} · {c.party}</div></td>
                        <td style={{ textAlign: "right" }}>{pos.value != null ? fmt(pos.value, cur) : "—"}</td>
                        <td>
                          <div className="pos-cert">{fmt(pos.certified, cur)}{pos.pct != null && <span className="pos-pct">{pos.pct.toFixed(1)}%</span>}</div>
                          {pos.pct != null && <div className="pos-bar"><span style={{ width: `${Math.min(100, Math.max(0, pos.pct))}%` }} className={pos.pct > 100 ? "over" : ""} /></div>}
                          {pos.advancePaid > 0 && <div className="muted" style={{ fontSize: 10 }}>+ advance {fmt(pos.advancePaid, cur)} (repayable, 14.2 — not counted)</div>}
                        </td>
                        <td style={{ textAlign: "right" }}><input className="pos-ret" type="number" value={numStr(c.retentionPercent)} onChange={(e) => patchContract(project.id, c.id, { retentionPercent: num(e.target.value) })} placeholder="0" /></td>
                        <td style={{ textAlign: "right" }}>{fmt(pos.retentionHeld, cur)}</td>
                        <td style={{ textAlign: "right" }}>{pos.remaining != null ? fmt(pos.remaining, cur) : "—"}</td>
                      </tr>
                    );
                  })}
                  {(() => {
                    const val = project.contracts.reduce((s, c) => s + (c.value != null && isFinite(c.value) ? c.value : 0), 0);
                    const cert = project.contracts.reduce((s, c) => s + worksCertified(c), 0); // works only — advances excluded
                    const adv = project.contracts.reduce((s, c) => s + advancePaid(c), 0);
                    const ret = project.contracts.reduce((s, c) => s + contractPosition(c).retentionHeld, 0);
                    return (
                      <tr className="pos-total">
                        <td>Project total</td>
                        <td style={{ textAlign: "right" }}>{val ? fmt(val, project.currency) : "—"}</td>
                        <td>{fmt(cert, project.currency)}{val ? <span className="pos-pct">{((cert / val) * 100).toFixed(1)}%</span> : null}{adv > 0 && <div className="muted" style={{ fontSize: 10 }}>+ advance {fmt(adv, project.currency)} (repayable)</div>}</td>
                        <td />
                        <td style={{ textAlign: "right" }}>{fmt(ret, project.currency)}</td>
                        <td style={{ textAlign: "right" }}>{val ? fmt(val - cert, project.currency) : "—"}</td>
                      </tr>
                    );
                  })()}
                </tbody>
              </table>
            </div>
          )}

          {/* By supplier */}
          {suppliers.length > 0 && (
            <div className="group">
              <div className="glabel">By payee / supplier {supplierFilter && <button className="link" onClick={() => setSupplierFilter(null)}>· clear filter</button>}</div>
              <table className="pay-table">
                <thead><tr><th>Payee / Supplier</th><th>Contracts</th><th>Certs</th><th style={{ textAlign: "right" }}>Total authorised</th><th style={{ textAlign: "right" }}>Statement</th></tr></thead>
                <tbody>
                  {suppliers.map((s) => (
                    <tr key={s.supplier} className={supplierFilter === s.supplier ? "on" : ""} onClick={() => setSupplierFilter(supplierFilter === s.supplier ? null : s.supplier)} style={{ cursor: "pointer" }}>
                      <td>{s.supplier}</td><td>{s.contracts}</td><td>{s.certs}</td><td style={{ textAlign: "right" }}>{fmt(s.total, project.currency)}</td>
                      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        <button className="mini" onClick={(e) => { e.stopPropagation(); genStatement("docx", s.supplier); }} title="Statement (Word)">DOC</button>
                        <button className="mini" onClick={(e) => { e.stopPropagation(); genStatement("pdf", s.supplier); }} title="Statement (PDF)">PDF</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Certificate editor */}
      {authPrompt !== null && <AuthPanel reason={authPrompt} onDone={onSignedIn} onCancel={() => setAuthPrompt(null)} />}

      {editing && project && (() => {
        const ct = project.contracts.find((c) => c.id === editing.contractId);
        if (!ct) return null;
        return (
          <CertEditor
            cert={editing.cert}
            contract={ct}
            isNew={!ct.certificates.some((x) => x.id === editing.cert.id)}
            currency={ct.currency || project.currency}
            onCancel={() => setEditing(null)}
            onSave={(cert) => { upsertCert(project.id, editing.contractId, cert); syncToAtelier(editing.contractId, cert); setEditing(null); }}
          />
        );
      })()}

      {sharing && project && (() => {
        const url = sharing.id ? `${location.origin}/?review=${sharing.id}` : "";
        // Read the live cert so a receipt pulled in from the link shows here.
        const live = project.contracts.find((x) => x.id === sharing.contractId)?.certificates.find((x) => x.id === sharing.cert.id) || sharing.cert;
        const c = sharing.cert;
        const forReceipt = !!sharing.forReceipt;
        const msg = forReceipt
          ? `Payment Certificate ${c.number || ""} — ${project.name}\nAmount: ${fmt(c.amount, c.currency)}\n\nPlease confirm you have received this payment: open the link, sign, and upload a photo of the receipt.\n${url}`
          : `Payment Certificate ${c.number || ""} — ${project.name}\nAmount: ${fmt(c.amount, c.currency)}\n\nPlease find the payment certificate above. Open this link to view it:\n${url}`;
        return (
          <div className="modal-back" onClick={() => { setSharing(null); setShareErr(null); }}>
            <div className="modal share-modal" onClick={(e) => e.stopPropagation()}>
              <div className="glabel">{forReceipt ? "Send for receipt of payment" : "Share payment certificate"}</div>
              <div className="muted" style={{ fontSize: 12.5, marginBottom: 10 }}>{c.number || "Certificate"} · {fmt(c.amount, c.currency)}{c.description ? ` — ${c.description}` : ""}</div>
              {sharing.busy || !url ? (
                <div className="share-actions"><span className="spinner" /> <span className="hint" style={{ margin: 0 }}>Preparing the live link…</span></div>
              ) : (
                <>
                  <div className="lbl">{forReceipt ? "Send this link to the payee to sign for receipt and upload the receipt photo" : "Live link — anyone with it can view this certificate"}</div>
                  <div className="share-row">
                    <input readOnly value={url} onFocus={(e) => e.currentTarget.select()} />
                    <button onClick={() => navigator.clipboard?.writeText(url).then(() => { setShareCopied(true); setTimeout(() => setShareCopied(false), 1500); })}>{shareCopied ? "Copied ✓" : "Copy"}</button>
                    <a className="btnlink" href={url} target="_blank" rel="noreferrer">Open</a>
                  </div>
                  <div className="share-row">
                    <ShareButtons url={url} subject={`${forReceipt ? "Please confirm receipt — " : ""}Payment Certificate ${c.number || ""} — ${project.name}`} message={msg} />
                  </div>
                  {forReceipt && (
                    live.receipt?.confirmedBy ? (
                      <div className="receipt-box done" style={{ marginTop: 8 }}>
                        <div className="lbl">✓ Receipt confirmed</div>
                        <div className="receipt-summary">
                          <div>Confirmed by <b>{live.receipt.confirmedBy}</b>{live.receipt.confirmedDate ? ` on ${live.receipt.confirmedDate}` : ""}.</div>
                          {live.receipt.signature && <img className="receipt-sig" src={live.receipt.signature} alt="signature" />}
                          {live.receipt.receiptPhoto && <a className="fattach-file" href={fileUrl(live.receipt.receiptPhoto.id)} target="_blank" rel="noreferrer">📎 {live.receipt.receiptPhoto.name}</a>}
                        </div>
                      </div>
                    ) : (
                      <div className="hint" style={{ margin: "8px 0 0" }}><span className="spinner" style={{ verticalAlign: "middle" }} /> Awaiting the payee's confirmation — it will appear here automatically.</div>
                    )
                  )}
                  {/* What's on the link besides the certificate. */}
                  <div className="inv-status">
                    {live.invoice ? (
                      <>
                        <span>📎 Invoice on this link: <a className="fattach-file" href={fileUrl(live.invoice.id)} target="_blank" rel="noreferrer">{live.invoice.name}</a> <span className="muted">{fileSize(live.invoice.size)}</span></span>
                      </>
                    ) : (
                      <span className="muted">No invoice attached yet — the contractor can attach it on this link and it will appear here, or attach it under Edit.</span>
                    )}
                  </div>
                  {/* Email it — a dated record of what went to whom is kept on the certificate. */}
                  <EmailPanel
                    project={project}
                    contract={project.contracts.find((x) => x.id === sharing.contractId)!}
                    cert={live}
                    url={url}
                    forReceipt={forReceipt}
                    onSent={(rec) => { const next = { ...live, emailLog: [...(live.emailLog || []), rec] }; upsertCert(project.id, sharing.contractId, next); syncToAtelier(sharing.contractId, next); }}
                  />
                  {!forReceipt && <div className="hint" style={{ margin: "2px 0 0" }}>Sharing again after editing the certificate refreshes this same link.</div>}
                </>
              )}
              {shareErr && <div className="hint" style={{ color: "#a3402f" }}>{shareErr}</div>}
              <div className="share-actions" style={{ marginTop: 10 }}>
                <button className="link" onClick={() => { setSharing(null); setShareErr(null); }}>Close</button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

/** Ids of every stored file a certificate references: the invoice and the
 * proof on each logged payment. */
function proofIds(c: PaymentCert): string[] {
  return [c.invoice?.id, ...(c.payments || []).map((p) => p.proof?.id)].filter((id): id is string => !!id);
}

/** Email a certificate (with its PDF and the invoice) to named stakeholders
 * through the server, so the ledger keeps a record of what went to whom. */
function EmailPanel({ project, contract, cert, url, forReceipt, onSent }: { project: PaymentProject; contract: Contract; cert: PaymentCert; url: string; forReceipt: boolean; onSent: (rec: SentEmail) => void }) {
  const payee = contract.party || "the payee";
  const [to, setTo] = useState(() => Array.from(new Set([...parseEmails(project.clientEmail || ""), ...parseEmails(contract.email || "")])).join(", "));
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState(`${forReceipt ? "Please confirm receipt — " : ""}Payment Certificate ${cert.number || ""} — ${project.name}`);
  const [text, setText] = useState(() =>
    [
      `Payment Certificate ${cert.number || ""} — ${project.name}`,
      `${contract.name} · ${payee}`,
      `Amount certified: ${fmt(cert.amount, cert.currency)}`,
      "",
      forReceipt
        ? "Please confirm you have received this payment: open the link, sign, and upload a photo of the receipt."
        : `Please find the payment certificate attached${cert.invoice ? ` together with the ${contract.kind === "supply" ? "supplier's" : "contractor's"} invoice` : ""}.`,
      `Open the live certificate here:`,
      url,
    ].join("\n"),
  );
  const [attachPdf, setAttachPdf] = useState(true);
  const [attachInvoice, setAttachInvoice] = useState(!!cert.invoice);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [open, setOpen] = useState((cert.emailLog || []).length === 0);
  async function send() {
    const recipients = parseEmails(to);
    if (!recipients.length) { setErr("Enter at least one email address."); return; }
    setBusy(true); setErr(null); setDone(null);
    try {
      const attachments: { name: string; contentBase64: string }[] = [];
      if (attachPdf) {
        const blob = await renderPdfBlob(assembleCertificate(project, contract, cert), "A4");
        attachments.push({ name: `${(cert.number || "Payment Certificate").replace(/[^\w.-]+/g, "_")} - ${project.name.replace(/[^\w.-]+/g, "_")}.pdf`, contentBase64: await blobToBase64(blob) });
      }
      const rec = await sendEmail({ to: recipients, cc: parseEmails(cc), subject, text, fileIds: attachInvoice && cert.invoice ? [cert.invoice.id] : [], attachments });
      onSent(rec);
      setDone(`Sent to ${rec.to.join(", ")}${rec.cc?.length ? ` · cc ${rec.cc.join(", ")}` : ""}${rec.attachments?.length ? ` · attached ${rec.attachments.join(", ")}` : ""}`);
    } catch (e: any) {
      setErr(String(e?.message || e));
    } finally {
      setBusy(false);
    }
  }
  const log = cert.emailLog || [];
  return (
    <div className="email-panel">
      <div className="lbl" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span>✉ Email to stakeholders{log.length ? <span className="muted" style={{ textTransform: "none", letterSpacing: 0 }}> · sent {log.length} time{log.length === 1 ? "" : "s"}</span> : null}</span>
        {!open && <button className="link" onClick={() => setOpen(true)}>Send again →</button>}
      </div>
      {open && (
        <>
          <div className="row">
            <label><span className="lbl">To</span><input value={to} onChange={(e) => setTo(e.target.value)} placeholder="client@example.com, contractor@example.com" /></label>
            <label><span className="lbl">Cc <span className="muted">(the studio is always copied)</span></span><input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="optional" /></label>
          </div>
          <label><span className="lbl">Subject</span><input value={subject} onChange={(e) => setSubject(e.target.value)} /></label>
          <label><span className="lbl">Message</span><textarea value={text} onChange={(e) => setText(e.target.value)} rows={5} /></label>
          <div className="email-opts">
            <label><input type="checkbox" checked={attachPdf} onChange={(e) => setAttachPdf(e.target.checked)} /> Attach the certificate (PDF)</label>
            <label title={cert.invoice ? cert.invoice.name : "No invoice attached to this certificate yet"}><input type="checkbox" checked={attachInvoice} disabled={!cert.invoice} onChange={(e) => setAttachInvoice(e.target.checked)} /> Attach the invoice{cert.invoice ? ` (${cert.invoice.name})` : ""}</label>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button className="dark sm" onClick={send} disabled={busy}>{busy ? <span className="spinner" /> : "Send email"}</button>
            {done && <span className="hint" style={{ margin: 0, color: "#2f7d4f" }}>{done}</span>}
            {err && <span className="hint" style={{ margin: 0, color: "#a3402f" }}>{err}</span>}
          </div>
        </>
      )}
      {log.length > 0 && (
        <div className="email-log">
          {[...log].reverse().slice(0, 6).map((e, i) => (
            <div key={e.id || i}><b>{e.at.slice(0, 16).replace("T", " ")}</b> → {e.to.join(", ")}{e.cc?.length ? ` · cc ${e.cc.join(", ")}` : ""} · {e.subject}{e.attachments?.length ? ` · 📎 ${e.attachments.length}` : ""}</div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Common codes offered in every currency box (free text still allowed). */
const CURRENCIES = ["GYD", "USD", "EUR", "GBP", "CAD", "TTD", "BBD", "XCD", "SRD", "BRL", "CNY", "INR", "AED"];

/** A currency code box that commits on blur / Enter (so a cascade prompt
 * doesn't fire on every keystroke). */
function CurrencyField({ value, onCommit, title }: { value: string; onCommit: (v: string) => void; title?: string }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  const commit = () => { if (draft.trim() && draft.trim().toUpperCase() !== value) onCommit(draft); else setDraft(value); };
  return (
    <label className="ccy-chip" title={title}>
      <span>Currency</span>
      <input list="ccy-list" value={draft} onChange={(e) => setDraft(e.target.value.toUpperCase())} onBlur={commit} onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); if (e.key === "Escape") { setDraft(value); (e.target as HTMLInputElement).blur(); } }} maxLength={5} />
    </label>
  );
}

/** Short series labels for the certificate rows. */
const SERIES_SHORT: Record<CertSeries, string> = {
  advance: "Advance (14.2)",
  interim: "Interim (IPC)",
  final: "Final",
  supply: "Supply",
  services: "Services",
  other: "Other",
};

/** "+ New certificate" — one click per series, pre-numbered from what the
 * contract already has, so a second advance or the next IPC is a single pick. */
function NewCertMenu({ contract, scanning, onPick, onScan }: { contract: Contract; scanning: boolean; onPick: (s: CertSeries) => void; onScan: () => void }) {
  const [open, setOpen] = useState(false);
  const series = seriesForContract(contract);
  return (
    <div className="ncm">
      <button className="sm dark" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        {scanning ? <span className="spinner" /> : "+ New certificate ▾"}
      </button>
      {open && (
        <>
          <div className="ncm-back" onClick={() => setOpen(false)} />
          <div className="ncm-list" role="menu">
            {series.map((s) => (
              <button key={s} className="ncm-item" role="menuitem" onClick={() => { setOpen(false); onPick(s); }}>
                <b>{CERT_SERIES_LABEL[s]}<span className="ncm-no">{nextCertNumber(contract, s)}</span></b>
                <span>{CERT_SERIES_HINT[s]}</span>
              </button>
            ))}
            <div className="ncm-sep" />
            <button className="ncm-item" role="menuitem" disabled={scanning} onClick={() => { setOpen(false); onScan(); }}>
              <b>⤒ Scan an invoice / payment request</b>
              <span>Reads the PDF and drafts the certificate from it — amount, reference and type.</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function CertEditor({ cert, contract, isNew, currency, onSave, onCancel }: { cert: PaymentCert; contract: Contract; isNew: boolean; currency: string; onSave: (c: PaymentCert) => void; onCancel: () => void }) {
  const [c, setC] = useState<PaymentCert>({ ...cert, currency: cert.currency || currency });
  const set = (p: Partial<PaymentCert>) => setC((x) => ({ ...x, ...p }));
  // Proof files upload immediately but this editor is a draft, so deletion is
  // deferred until the edit is actually saved or abandoned.
  const proofFiles = useDiscardedFiles(proofIds(cert));
  const save = () => { proofFiles.commit(proofIds(c)); onSave(c); };
  const cancel = () => {
    proofFiles.discard();
    // A new draft's files (e.g. the scanned invoice uploaded before the editor
    // opened) have no owner once the draft is abandoned — remove them.
    if (isNew) for (const id of proofIds(cert)) deleteFile(id).catch(() => {});
    onCancel();
  };
  const [showPay, setShowPay] = useState((cert.payments || []).length > 0);
  const series = certSeries(c);
  // Interim/final: either type the amount, or build it up from the Contractor's
  // Statement (14.3) — then the amount is computed and the printed certificate
  // carries the full Clause 14 tables.
  const [useBuildup, setUseBuildup] = useState(hasValuation(cert.buildup));
  const round2 = (x: number) => Math.round(x * 100) / 100;
  const setBu = (p: Partial<CertBuildup>) =>
    setC((x) => {
      const next: PaymentCert = { ...x, buildup: { ...(x.buildup || {}), ...p } };
      return hasValuation(next.buildup) ? { ...next, amount: round2(computeBuildup(contract, next).totalPayable) } : next;
    });
  function startBuildup() {
    setUseBuildup(true);
    setC((x) => ({ ...x, buildup: { retentionPercent: contract.retentionPercent, ...(x.buildup || {}), worksExecuted: x.buildup?.worksExecuted ?? (isFinite(x.amount) && x.amount > 0 ? x.amount : NaN) } }));
  }
  function stopBuildup() {
    setUseBuildup(false);
    // Keep the particulars (statement, period, engineer); drop the valuation so
    // the typed amount is authoritative again.
    setC((x) => { const { worksExecuted, materialsOnSite, variations, provisionalSums, dayworks, priceAdjustment, otherAdditions, ...keep } = x.buildup || {}; return { ...x, buildup: keep }; });
  }
  const buSummary = (series === "interim" || series === "final") && useBuildup && hasValuation(c.buildup) ? computeBuildup(contract, c) : null;
  const isSupply = series === "supply";
  const isFidic = series === "advance" || series === "interim" || series === "final";
  const cur = c.currency || currency;
  const money = (n: number) => `${cur} ${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const amountInput = useRef<HTMLInputElement>(null);
  // A fresh draft opens straight on the amount — that's the one thing you type.
  useEffect(() => { if (isNew) amountInput.current?.focus(); }, [isNew]);

  // Switching series: a NEW draft is re-templated (number, wording, kind); an
  // existing certificate only changes kind, and gains the word "advance" if it
  // becomes one, so its number and text are never rewritten behind your back.
  const seriesChoices = Array.from(new Set<CertSeries>([...seriesForContract(contract), series]));
  function pickSeries(s: CertSeries) {
    if (s === series) return;
    if (isNew) { setC(applySeries(c, s, contract)); return; }
    const kind: CertKind = s === "supply" ? "material-supply" : s === "services" ? "services" : s === "other" ? "other" : "construction";
    const patch: Partial<PaymentCert> = { kind };
    if (s === "advance" && !isAdvanceCert({ ...c, kind })) patch.description = `Advance payment — ${c.description || ""}`.trim();
    if (s !== "advance" && isAdvanceCert({ ...c, kind })) patch.description = c.description.replace(/\badvance( payment)?\b\s*[—–-]?\s*/i, "").trim();
    if (s === "final" && !/\bfinal\b/i.test(c.description)) patch.description = `Final payment — ${c.description || ""}`.trim();
    set(patch);
  }

  // Build-up context from the contract's OTHER certificates — the same arithmetic
  // the printed certificate uses, so what you see here is what the document says.
  const others = contract.certificates.filter((x) => x.id !== c.id);
  const sum = (xs: PaymentCert[]) => xs.reduce((s, x) => s + (isFinite(x.amount) ? x.amount : 0), 0);
  const worksBefore = sum(others.filter((x) => !isAdvanceCert(x)));
  const advBefore = sum(others.filter(isAdvanceCert));
  const advCount = others.filter(isAdvanceCert).length;
  const value = contract.value != null && isFinite(contract.value) ? contract.value : null;
  const amt = isFinite(c.amount) ? c.amount : 0;
  const retPct = contract.retentionPercent || 0;
  const pctOf = (n: number) => (value ? ` · ${((n / value) * 100).toFixed(1)}%` : "");
  const titleFor = isNew ? `New certificate — ${CERT_SERIES_LABEL[series]}` : `Edit ${c.number || "certificate"} — ${CERT_SERIES_LABEL[series]}`;

  return (
    <div className="modal-back" onClick={cancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="glabel">{titleFor} <span className="muted" style={{ textTransform: "none", letterSpacing: 0, fontWeight: 400 }}>· {contract.name || "contract"} — {contract.party}</span></div>
        <div className="series-seg" role="tablist" aria-label="Certificate type">
          {seriesChoices.map((s) => (
            <button key={s} className={s === series ? "on" : ""} onClick={() => pickSeries(s)} title={CERT_SERIES_HINT[s]}>{CERT_SERIES_LABEL[s]}</button>
          ))}
        </div>

        <div className="row-3">
          <label><span className="lbl">Certificate no.</span><input value={c.number} onChange={(e) => set({ number: e.target.value })} /></label>
          <label><span className="lbl">Date</span><input value={c.date} onChange={(e) => set({ date: e.target.value })} /></label>
          <label><span className="lbl">Status</span>
            <select value={c.status} onChange={(e) => set({ status: e.target.value as CertStatus })}>
              {(["draft", "authorised", "acknowledged", "paid"] as CertStatus[]).map((s) => <option key={s} value={s}>{CERT_STATUS_LABEL[s]}</option>)}
            </select>
          </label>
        </div>
        {(series === "interim" || series === "final") && (
          <div className="bu-mode">
            <button className={!useBuildup ? "on" : ""} onClick={stopBuildup}>Enter the amount</button>
            <button className={useBuildup ? "on" : ""} onClick={startBuildup} title="Value the Works to date from the Contractor's Statement; retention, advance recovery and previous certificates are deducted automatically">Build up from the Statement (14.3)</button>
          </div>
        )}
        <div className="row" style={{ gridTemplateColumns: "2fr 1fr" }}>
          <label><span className="lbl">Amount certified{buSummary ? <span className="muted"> · computed from the build-up{c.buildup?.vatPercent ? ", incl. VAT" : ""}</span> : null}</span>
            <input ref={amountInput} type="number" value={numStr(c.amount)} onChange={(e) => set({ amount: num(e.target.value) })} placeholder="0.00" readOnly={!!buSummary} style={{ fontSize: 16, fontVariantNumeric: "tabular-nums", ...(buSummary ? { background: "#f4f3ef" } : {}) }} />
          </label>
          <label><span className="lbl">Currency</span><input list="ccy-list" value={c.currency} onChange={(e) => set({ currency: e.target.value.toUpperCase() })} placeholder={currency} /></label>
        </div>

        {/* Clause 14 build-up (interim/final) — the Contractor's Statement figures,
            cumulative to date, from which the amount due is derived. */}
        {(series === "interim" || series === "final") && useBuildup && (() => {
          const bu = c.buildup || {};
          const prevAuto = previouslyCertifiedAuto(contract, c);
          const nf = (v?: number) => numStr(v);
          return (
            <div className="bu-box">
              <div className="cert-ctx-title">A · Valuation of the Works to date (14.3), cumulative</div>
              <div className="row-3">
                <label><span className="lbl">Works executed (14.3a)</span><input type="number" value={nf(bu.worksExecuted)} onChange={(e) => setBu({ worksExecuted: num(e.target.value) })} /></label>
                <label><span className="lbl">Plant &amp; Materials (14.5)</span><input type="number" value={nf(bu.materialsOnSite)} onChange={(e) => setBu({ materialsOnSite: num(e.target.value) })} /></label>
                <label><span className="lbl">Variations (13.3)</span><input type="number" value={nf(bu.variations)} onChange={(e) => setBu({ variations: num(e.target.value) })} /></label>
              </div>
              <div className="row-3">
                <label><span className="lbl">Provisional Sums (13.4)</span><input type="number" value={nf(bu.provisionalSums)} onChange={(e) => setBu({ provisionalSums: num(e.target.value) })} /></label>
                <label><span className="lbl">Dayworks (13.5)</span><input type="number" value={nf(bu.dayworks)} onChange={(e) => setBu({ dayworks: num(e.target.value) })} /></label>
                <label><span className="lbl">Adjustments (13.6 / 13.7)</span><input type="number" value={nf(bu.priceAdjustment)} onChange={(e) => setBu({ priceAdjustment: num(e.target.value) })} /></label>
              </div>
              <div className="cert-ctx-title">B · Retention, advance recovery &amp; previous certificates</div>
              <div className="row-3">
                <label><span className="lbl">Retention %</span><input type="number" value={nf(bu.retentionPercent)} onChange={(e) => setBu({ retentionPercent: num(e.target.value) })} placeholder={numStr(contract.retentionPercent) || "0"} /></label>
                <label><span className="lbl">Retention limit % of contract</span><input type="number" value={nf(bu.retentionLimitPercent)} onChange={(e) => setBu({ retentionLimitPercent: num(e.target.value) })} /></label>
                <label><span className="lbl">Retention released (14.9)</span><input type="number" value={nf(bu.retentionReleased)} onChange={(e) => setBu({ retentionReleased: num(e.target.value) })} /></label>
              </div>
              <div className="row-3">
                <label><span className="lbl">Advance recovered to date (14.2.3)</span><input type="number" value={nf(bu.advanceRecoveredToDate)} onChange={(e) => setBu({ advanceRecoveredToDate: num(e.target.value) })} /></label>
                <label><span className="lbl">Previously certified (14.3g) <span className="muted">· blank = from ledger</span></span><input type="number" value={nf(bu.previouslyCertified)} onChange={(e) => setBu({ previouslyCertified: num(e.target.value) })} placeholder={money(prevAuto)} /></label>
                <label><span className="lbl">VAT %</span><input type="number" value={nf(bu.vatPercent)} onChange={(e) => setBu({ vatPercent: num(e.target.value) })} /></label>
              </div>
              <div className="cert-ctx-title">Contractor's Statement</div>
              <div className="row-3">
                <label><span className="lbl">Statement ref</span><input value={bu.statementRef || ""} onChange={(e) => setBu({ statementRef: e.target.value })} /></label>
                <label><span className="lbl">Period from</span><input value={bu.periodFrom || ""} onChange={(e) => setBu({ periodFrom: e.target.value })} /></label>
                <label><span className="lbl">Period to</span><input value={bu.periodTo || ""} onChange={(e) => setBu({ periodTo: e.target.value })} /></label>
              </div>
              {buSummary && (
                <div className="cert-ctx" style={{ margin: "6px 0 0" }}>
                  <div className="cert-ctx-row"><span>Gross value of work done to date</span><span>{money(buSummary.gross)}{pctOf(buSummary.gross)}</span></div>
                  <div className="cert-ctx-row"><span>Less retention held</span><span>({money(buSummary.retentionHeld)})</span></div>
                  {buSummary.advanceRecovered > 0 && <div className="cert-ctx-row"><span>Less advance recovered</span><span>({money(buSummary.advanceRecovered)})</span></div>}
                  <div className="cert-ctx-row"><span>Net value certified to date</span><span>{money(buSummary.netToDate)}</span></div>
                  <div className="cert-ctx-row"><span>Less previously certified{buSummary.previouslyAuto ? <span className="muted"> (from the ledger)</span> : null}</span><span>({money(buSummary.previouslyCertified)})</span></div>
                  {buSummary.vat > 0 && <div className="cert-ctx-row"><span>Add VAT at {bu.vatPercent}%</span><span>{money(buSummary.vat)}</span></div>}
                  <div className="cert-ctx-row total"><span>Amount due this certificate</span><span>{money(buSummary.totalPayable)}</span></div>
                  {advBefore > 0 && buSummary.advanceRecovered <= 0 && <div className="hint">Advance of {money(advBefore)} is outstanding under 14.2 — enter the cumulative amount recovered to date above once recovery starts.</div>}
                </div>
              )}
            </div>
          );
        })()}
        {series === "advance" && (
          <div className="row-3">
            <label><span className="lbl">Advance % of contract</span><input type="number" value={numStr(c.buildup?.advancePercent)} onChange={(e) => setBu({ advancePercent: num(e.target.value) })} /></label>
            <label><span className="lbl">Installment no.</span><input type="number" value={numStr(c.buildup?.installmentIndex)} onChange={(e) => setBu({ installmentIndex: num(e.target.value) })} placeholder={String(advCount + 1)} /></label>
            <label><span className="lbl">Of (total installments)</span><input type="number" value={numStr(c.buildup?.installmentCount)} onChange={(e) => setBu({ installmentCount: num(e.target.value) })} /></label>
          </div>
        )}
        {series === "advance" && (
          <label><span className="lbl">Advance Payment Guarantee ref (14.2.1)</span><input value={c.buildup?.guaranteeRef || ""} onChange={(e) => setBu({ guaranteeRef: e.target.value })} /></label>
        )}

        {/* Build-up — how this certificate sits against the contract. */}
        {series === "advance" && (
          <div className="cert-ctx">
            <div className="cert-ctx-title">Advance payment build-up (Sub-Clause 14.2)</div>
            {value != null && <div className="cert-ctx-row"><span>Accepted Contract Amount</span><span>{money(value)}</span></div>}
            <div className="cert-ctx-row"><span>Advance previously paid{advCount ? ` (${advCount} certificate${advCount === 1 ? "" : "s"})` : ""}</span><span>{money(advBefore)}</span></div>
            <div className="cert-ctx-row"><span>This certificate</span><span>{money(amt)}</span></div>
            <div className="cert-ctx-row total"><span>Total advance paid to date</span><span>{money(advBefore + amt)}<span className="muted">{pctOf(advBefore + amt)}</span></span></div>
            <div className="hint">Repayable — no retention, not counted against the contract value, recovered by deductions from later IPCs (14.2.3).</div>
          </div>
        )}
        {(series === "interim" || series === "final") && !useBuildup && (
          <div className="cert-ctx">
            <div className="cert-ctx-title">{series === "final" ? "Final" : "Interim"} payment build-up (Sub-Clause {series === "final" ? "14.13" : "14.6"})</div>
            {value != null && <div className="cert-ctx-row"><span>Accepted Contract Amount</span><span>{money(value)}</span></div>}
            <div className="cert-ctx-row"><span>Previously certified (works)</span><span>{money(worksBefore)}<span className="muted">{pctOf(worksBefore)}</span></span></div>
            <div className="cert-ctx-row"><span>This certificate</span><span>{money(amt)}</span></div>
            <div className="cert-ctx-row total"><span>Certified to date</span><span>{money(worksBefore + amt)}<span className="muted">{pctOf(worksBefore + amt)}</span></span></div>
            {retPct > 0 && <div className="cert-ctx-row"><span>Retention held to date at {retPct}%</span><span>{money((retPct / 100) * (worksBefore + amt))}</span></div>}
            {value != null && <div className="cert-ctx-row"><span>Balance of contract value remaining</span><span className={value - worksBefore - amt < 0 ? "over" : ""}>{money(value - worksBefore - amt)}</span></div>}
            {advBefore > 0 && <div className="hint">Advance of {money(advBefore)} paid under 14.2 is outstanding for recovery — net any recovery off the amount certified above.</div>}
          </div>
        )}
        {!isFidic && value != null && amt > 0 && value > amt + 0.5 && (
          <div className="cert-ctx">
            <div className="cert-ctx-title">Part payment of a {money(value)} contract</div>
            <div className="cert-ctx-row"><span>Previously certified</span><span>{money(worksBefore)}</span></div>
            <div className="cert-ctx-row"><span>This certificate</span><span>{money(amt)}{pctOf(amt)}</span></div>
            <div className="cert-ctx-row total"><span>Left to certify</span><span>{money(Math.max(0, value - worksBefore - amt))}</span></div>
          </div>
        )}

        <label><span className="lbl">Description of {isSupply ? "materials / goods" : series === "services" ? "services" : "works"}</span><textarea value={c.description} onChange={(e) => set({ description: e.target.value })} rows={2} /></label>
        {series === "advance" && !isAdvanceCert(c) && <div className="hint" style={{ color: "#a3402f", margin: "-6px 0 10px" }}>Keep the word “advance” in the description — that is what marks this as a repayable advance in the ledger and on the printed certificate.</div>}
        {isSupply && (
          <div className="row"><label><span className="lbl">Supplier invoice no.</span><input value={c.invoiceRef || ""} onChange={(e) => set({ invoiceRef: e.target.value })} /></label>
            <label><span className="lbl">Invoice date</span><input value={c.invoiceDate || ""} onChange={(e) => set({ invoiceDate: e.target.value })} /></label></div>
        )}
        {/* The payee's invoice / statement — shared and emailed with the certificate. */}
        <div className="inv-status">
          <span className="lbl" style={{ margin: 0 }}>{isSupply ? "Supplier's invoice" : "Contractor's invoice / statement"}</span>
          <FileAttach
            mini
            deferDelete
            file={c.invoice}
            label={isSupply ? "Attach the invoice" : "Attach the invoice / statement"}
            title="PDF or image. It goes out with the certificate: on the share link and as an email attachment."
            onChange={(f) => { proofFiles.track(f); set({ invoice: f }); }}
          />
          {!c.invoice && <span className="muted" style={{ fontSize: 11 }}>or the contractor can attach it on the share link</span>}
        </div>
        {/* Payments received — the open/partial/paid state is derived from these,
            so the badge can never disagree with the money actually logged. Folded
            away on a new certificate: nothing has been paid against it yet. */}
        <div className="pay-log">
          <div className="pay-log-head">
            <span className="lbl" style={{ margin: 0 }}>Payments received</span>
            <span className={`pay-state p-${certPayState(c)}`}>{PAY_STATE_LABEL[certPayState(c)]}</span>
            <span className="muted" style={{ fontSize: 11.5 }}>
              {fmt(certPaid(c), c.currency)} of {fmt(c.amount, c.currency)}
              {certOutstanding(c) > 0.5 && ` · ${fmt(certOutstanding(c), c.currency)} outstanding`}
            </span>
            {!showPay && <button className="link" style={{ marginLeft: "auto" }} onClick={() => setShowPay(true)}>Log a payment →</button>}
          </div>
          {showPay && (c.payments || []).map((p) => (
            <div className="pay-line-wrap" key={p.id}>
              <div className="pay-line">
                <input type="number" value={numStr(p.amount)} onChange={(e) => set({ payments: (c.payments || []).map((x) => (x.id === p.id ? { ...x, amount: num(e.target.value) } : x)) })} placeholder="amount" />
                <input value={p.date} onChange={(e) => set({ payments: (c.payments || []).map((x) => (x.id === p.id ? { ...x, date: e.target.value } : x)) })} placeholder="date" />
                <input value={p.ref || ""} onChange={(e) => set({ payments: (c.payments || []).map((x) => (x.id === p.id ? { ...x, ref: e.target.value } : x)) })} placeholder="method / reference" />
                <button className="chip-x" onClick={() => set({ payments: (c.payments || []).filter((x) => x.id !== p.id) })} title="Remove this payment">✕</button>
              </div>
              <FileAttach
                mini
                deferDelete
                file={p.proof}
                label="Attach proof"
                title="Attach the bank receipt or the payee's signed acknowledgement for this payment (PDF or image)"
                onChange={(f) => {
                  proofFiles.track(f);
                  set({ payments: (c.payments || []).map((x) => (x.id === p.id ? { ...x, proof: f } : x)) });
                }}
              />
            </div>
          ))}
          {showPay && (
            <button
              className="mini"
              onClick={() => {
                const rest = certOutstanding(c);
                const next: CertPayment = { id: uid(), amount: rest > 0.5 ? rest : NaN, date: nowDate(), ref: "" };
                set({ payments: [...(c.payments || []), next] });
              }}
              title="Log a payment received against this certificate — defaults to the outstanding balance"
            >
              + Log payment{certOutstanding(c) > 0.5 ? ` (${fmt(certOutstanding(c), c.currency)} outstanding)` : ""}
            </button>
          )}
        </div>
        <details className="cert-more" open={!isNew && !!(c.acknowledgedBy || c.reserved || c.notes)}>
          <summary className="muted" style={{ cursor: "pointer", fontSize: 11.5, margin: "4px 0 8px" }}>Acknowledgement, reservations &amp; notes</summary>
          <label style={{ display: "flex", gap: 8, alignItems: "center", margin: "4px 0 10px" }}>
            <input type="checkbox" style={{ width: "auto" }} checked={c.coversAllOutstanding} onChange={(e) => set({ coversAllOutstanding: e.target.checked })} />
            <span className="lbl" style={{ margin: 0 }}>Include payee acknowledgement (settles all outstanding to date)</span>
          </label>
          <div className="row">
            <label><span className="lbl">Acknowledged by (name)</span><input value={c.acknowledgedBy || ""} onChange={(e) => set({ acknowledgedBy: e.target.value })} /></label>
            <label><span className="lbl">Amounts reserved (optional)</span><input value={c.reserved || ""} onChange={(e) => set({ reserved: e.target.value })} /></label>
          </div>
          <label><span className="lbl">Notes (optional)</span><textarea value={c.notes || ""} onChange={(e) => set({ notes: e.target.value })} rows={2} /></label>
        </details>
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <button className="dark" onClick={save} disabled={!isFinite(c.amount) || c.amount <= 0} title={!isFinite(c.amount) || c.amount <= 0 ? "Enter the amount certified" : undefined}>{isNew ? "Create certificate" : "Save certificate"}</button>
          <button onClick={cancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
